import { createSelector, createSlice, current } from "@reduxjs/toolkit";
import dayjs from "dayjs";

import {
  getPKSThunk,
  createPKSThunk,
  updatePKSThunk,
  deletePKSThunk,
  verifyPKSByHeadThunk,
  getPKSByIdThunk,
  bulkCreatePKSThunk,
  //   updateStatusPKSThunk,
} from "state/action/pks";
import {
  getGrantFeePeriodsDayjs,
  getYearlySettingsTemplate,
} from "utils/dateUtils";

export const initialDigitalPaymentFee = {
  id: "",
  fee_type: "", // "MDR" | "Biaya Implementasi" | "Biaya Pemeliharaan" | "Lainnya"
  description: "",
  calculation_base: "", // "Per Transaksi" | "Flat Monthly" | "Percentage Revenue"
  rate: {
    type: "percentage", // "percentage" | "flat"
    value: 0, // angka persen atau nominal flat
    currency: "IDR",
  },
  billing: {
    billing_period: "", // "Monthly" | "Quarterly"
    billing_date: "",
    due_date: "",
    revenue_recognition_date: "",
  },
  total_price: 0,
};

// ─────────────────────────────────────────────────────────────────────────────
//  INITIAL CHANNEL — Setiap kanal pembayaran (Gate, Vending Machine, dll)
//  dalam satu service Digital Payment
// ─────────────────────────────────────────────────────────────────────────────
export const initialPaymentChannel = {
  id: "",
  channel_name: "", // "Gate Otomatis" | "Vending Machine" | "Mobile App" | "Web"
  channel_type: "", // "On-Premises" | "Digital"
  terminal_type: "", // "EDC" | "Gate Reader" | "QR Scanner" | "Tap on Phone"
  locations: [
    {
      station_id: "",
      station_name: "", // "Lebak Bulus" | "Fatmawati" | dst
      terminal_count: 0,
      terminal_ids: [],
    },
  ],
  accepted_instruments: [], // ["Kartu Flazz"] | ["e-money Mandiri"] | ["JakCard"] dst
  transaction_limit: {
    min_amount: 0,
    max_amount: 0,
    daily_limit: 0,
  },
  settlement: {
    settlement_period: "", // "T+0" | "T+1" | "T+2"
    settlement_bank: "",
    settlement_account: "",
    cut_off_time: "", // "23:59"
  },
};

// ─────────────────────────────────────────────────────────────────────────────
//  INITIAL REVENUE SHARING — Skema bagi hasil antara MRT & Bank DKI
// ─────────────────────────────────────────────────────────────────────────────
export const initialRevenueSharing = {
  scheme_type: "", // "Revenue Share" | "Fixed Fee" | "Hybrid"
  base_calculation: "", // "Gross Revenue" | "Net Revenue" | "Transaction Value"
  parties: [
    {
      party: "provider", // PT MRT Jakarta
      percentage: 0,
      description: "",
    },
    {
      party: "client", // PT Bank DKI
      percentage: 0,
      description: "",
    },
  ],
  minimum_guarantee: {
    is_active: false,
    amount: 0,
    period: "monthly",
  },
  reporting: {
    recapitulation_date: "", // tanggal rekap penjualan per bulan
    report_format: "",
    submission_channel: "", // "Email" | "Portal" | "SFTP"
  },
};

// Helper function untuk menentukan struktur 'details'
const getDetailsStructure = (itemType) => {
  switch (itemType) {
    case "Consulting":
      return {
        service: "",
        item: "",
      };
    case "Telecomunication":
      return {
        umkm_category: "",
        // station_code: "",
        // contract_period_start: "",
        // contract_period_end: "",
      };
    case "Digital Payment":
      return {
        // ── Instrumen yang diterima ─────────────────────────────────────────────
        accepted_instruments: [
          // "Kartu Flazz BCA" | "e-money Mandiri" | "Brizzi BRI"
          // "JakCard Bank DKI" | "TapCash BNI" | "Mega Cash"
        ],
        instrument_issuer: "", // "Bank DKI" — penerbit utama dalam PKS ini

        // ── Kanal Pembayaran ────────────────────────────────────────────────────
        payment_channels: [{ ...initialPaymentChannel }],

        // ── Skema Revenue ───────────────────────────────────────────────────────
        revenue_sharing: { ...initialRevenueSharing },

        // ── Fee & Biaya ─────────────────────────────────────────────────────────
        fees: [{ ...initialDigitalPaymentFee }],

        // ── Target Transaksi (KPI) ──────────────────────────────────────────────
        transaction_target: {
          is_active: false,
          daily_target: 0,
          monthly_target: 0,
          target_review_period: "quarterly", // "monthly" | "quarterly" | "yearly"
        },

        // ── Kewajiban Teknis ────────────────────────────────────────────────────
        technical_requirements: {
          // Uptime sistem pembayaran
          system_uptime_percentage: 99.9,
          // Waktu maksimal penyelesaian jika terjadi gangguan
          max_downtime_hours: 4,
          // Standar keamanan
          security_standard: "PCI-DSS", // "PCI-DSS" | "ISO 27001"
          // Integrasi
          integration_method: "", // "API" | "SDK" | "Host-to-Host"
          integration_spec_url: "",
        },

        // ── Implementasi & Go-Live ──────────────────────────────────────────────
        implementation: {
          pilot_station: "", // Stasiun uji coba pertama
          pilot_start_date: "",
          pilot_end_date: "",
          rollout_plan: [
            {
              station_id: "",
              station_name: "",
              target_go_live: "",
              actual_go_live: "",
              status: "", // "Planned" | "In Progress" | "Live" | "Delayed"
            },
          ],
        },

        // ── Rekonsiliasi & Pelaporan ────────────────────────────────────────────
        reconciliation: {
          method: "", // "Otomatis" | "Manual" | "Hybrid"
          frequency: "daily", // "daily" | "weekly" | "monthly"
          reconciliation_time: "", // "06:00"
          dispute_resolution_days: 3, // hari kerja untuk penyelesaian selisih
          contact_person: {
            mrt: { name: "", email: "", phone: "" },
            bank: { name: "", email: "", phone: "" },
          },
        },
      };
    case "Retail":
      return {
        properties: [
          {
            property: "",
            building: "",
            floor: "",
            unit: [],
            fitting_in: "",
            fitting_out: "",
            initial_handover: "",
            start_date: "",
            end_property_date: "",
            duration: "",
          },
        ],
        category_industri: "",
        retail_type: "",
        retail_location_category: "",
        // return_handover: "",
      };
    case "Retail Station":
      return {
        properties: [
          {
            property: "",
            building: "",
            floor: "",
            unit: [],
            fitting_in: "",
            fitting_out: "",
            initial_handover: "",
            start_date: "",
            end_property_date: "",
            duration: "",
            base_price: "",
            base_price_source: "",
            price_unit_offer: [],
            base_price_snapshot: {
              property: null,
              building: null,
              floor: null,
              unit: null,
            },
            // costs: [],
          },
        ],
        financials: {
          cost_responsibility: "",
          revenue_type: null,
          details: {},
        },
        category_industri: "",
        retail_type: "",
        description: "",
        // return_handover: "",
      };
    case "Retail Non Station": {
      return {
        properties: [
          {
            property: "",
            building: "",
            floor: "",
            unit: [],
            fitting_in: "",
            fitting_out: "",
            initial_handover: "",
            start_date: "",
            end_property_date: "",
            duration: "",
            base_price: "",
            base_price_source: "",
            price_unit_offer: [],
            base_price_snapshot: {
              property: null,
              building: null,
              floor: null,
              unit: null,
            },
            costs: [
              // {
              //   cost_type: "",
              //   description: "",
              //   details: {},
              // },
            ],
          },
        ],
        financials: {
          cost_responsibility: "",
          revenue_type: null,
          details: {},
        },
        category_industri: "",
        retail_type: "",
        description: "",
        // return_handover: "",
      };
    }
    case "Retail Blok-M Hub":
      return {
        properties: [
          {
            property: "",
            building: "",
            floor: "",
            unit: [],
            fitting_in: "",
            fitting_out: "",
            initial_handover: "",
            start_date: "",
            end_property_date: "",
            duration: "",
            base_price: "",
            costs: [
              {
                cost_type: "Rental Fee",
                evidence: "",
                paymentType: "periodic",
                periodic: {
                  frequency: "daily",
                  daily: { hour: "08" },
                  billing_config: {
                    billing_offset_days: 0,
                    due_offset_days: 14,
                    revenue_recognition_offset_days: 30,
                  },
                },
                nonPeriodic: null,
                details: {},
              },
            ],
            summary: {
              subtotal: 0,
              discounts: [],
              taxes: [
                {
                  tax_name: "PPN",
                  rate_percent: 11,
                  amount: 0,
                },
              ],
              grand_total: 0,
            },
          },
        ],
        financials: {
          cost_responsibility: "",
          revenue_type: null,
          details: {},
        },
        category_industri: "",
        retail_type: "",
        description: "",
        // return_handover: "",
      };
    case "Retail Transport Hub":
      return {
        properties: [
          {
            property: "",
            building: "",
            floor: "",
            unit: [],
            fitting_in: "",
            fitting_out: "",
            initial_handover: "",
            start_date: "",
            end_property_date: "",
            duration: "",
          },
        ],
        category_industri: "",
        retail_type: "",
        // retail_location_category: "",
        // return_handover: "",
      };
    case "Naming Rights":
      return {
        hak_penamaan: {
          nama_asli: "",
          nama_baru: "", // Dikosongkan karena harus diisi (required)
        },
        branding_fisik_stasiun: {
          entrance_gate: true,
          platform_name: true,
          platform_screen_door: true,
        },
        media_informasi_penumpang: {
          pid_ratangga: true,
          voice_over_ratangga: true,
          pid_stasiun: true,
        },
        signage_dan_map: {
          line_map_area_sekitar: true,
          map_stasiun: true,
          map_tarif: true,
          map_sistem_tangga: true,
          peta_jalur_mrt_jakarta: true,
          peta_jaringan_ka_perkotaan: true,
        },
        aktivasi_dan_pr: {
          pr_value: true,
          aktivasi_offline_launching: true,
          booth_aktivasi: {
            diberikan: true,
            dimensi: "2m x 2m",
            durasi_hari_per_tahun: 14,
            fasilitas_jaringan_listrik: true,
            keterangan:
              "Frekuensi pelaksanaan sesuai kebutuhan BANK DKI setiap tahunnya",
          },
        },
        platform_digital: {
          mrtj_apps: true,
          website_dan_microsite: true,
          instagram: true,
          facebook: true,
        },
        ketentuan_biaya: {
          termasuk_pajak_reklame: true,
        },
        costs: [],
      };
    case "Casual Lease":
      return {
        location: "",
        event_dates: "",
      };
    case "Advertising":
      return {
        location_category: "",
        // advertisement_type: "",
        // spesification: "",
      };
    case "Activation":
      return {
        // ── Informasi Umum Aktivasi ─────────────────────────────────────────────
        activation_type: "", // "Booth Pameran" | "Branding Stasiun" | "Instalasi Seni" | "Event Promosi" | "Kombinasi"
        activation_name: "", // Nama aktivasi/event
        brand_name: "", // Nama brand yang melakukan aktivasi
        campaign_theme: "", // Tema kampanye

        // ── Lokasi & Area ───────────────────────────────────────────────────────
        locations: [
          {
            station_id: "",
            station_name: "", // "Lebak Bulus" | "Fatmawati" | dst
            areas: [
              {
                area_type: "", // "Concourse" | "Platform" | "Entrance" | "Corridor" | "Paid Area" | "Non-Paid Area"
                area_code: "", // Kode area spesifik
                area_size: {
                  length: 0, // meter
                  width: 0, // meter
                  height: 0, // meter (untuk instalasi)
                  total_sqm: 0, // m²
                },
                floor_level: "", // "B1" | "Ground" | "Mezzanine"
                accessibility: "", // "Public" | "Restricted" | "Paid Zone Only"
              },
            ],
          },
        ],

        // ── Periode Aktivasi ────────────────────────────────────────────────────
        activation_period: {
          start_date: "", // Tanggal mulai aktivasi
          end_date: "", // Tanggal selesai aktivasi
          duration_days: 0, // Durasi dalam hari
          setup_date: "", // Tanggal mulai setup/instalasi
          dismantling_date: "", // Tanggal pembongkaran
          operational_hours: {
            start_time: "06:00", // Jam operasional mulai
            end_time: "23:00", // Jam operasional selesai
            days: [], // ["Monday", "Tuesday", ...] atau "All Days"
          },
        },

        // ── Detail Aktivasi per Tipe ────────────────────────────────────────────
        activation_details: {
          // Untuk Booth Pameran
          booth: {
            is_active: false,
            booth_type: "", // "Standard" | "Premium" | "Custom"
            booth_size: "", // "2x2m" | "3x3m" | "4x4m" | "Custom"
            booth_design: "", // "Open" | "Closed" | "Semi-Open"
            facilities_provided: {
              electricity: false, // Listrik disediakan
              electricity_capacity: 0, // Watt
              internet_wifi: false,
              water_supply: false,
              lighting: false,
              furniture: [], // ["Table", "Chair", "Display Rack"]
            },
            staff_count: 0, // Jumlah staff yang bertugas
            product_category: "", // Kategori produk yang dipamerkan
          },

          // Untuk Branding Stasiun
          branding: {
            is_active: false,
            branding_elements: [
              {
                element_type: "", // "Wall Branding" | "Floor Sticker" | "Pillar Wrap" | "Ceiling Banner" | "Digital Screen"
                location: "", // Lokasi spesifik
                dimensions: "", // Dimensi
                quantity: 0,
                material: "", // "Vinyl" | "Sticker" | "Banner" | "LED"
                installation_method: "", // "Adhesive" | "Frame" | "Hanging"
              },
            ],
            brand_visibility_duration: 0, // Hari
            removal_responsibility: "", // "Client" | "MRT" | "Shared"
          },

          // Untuk Instalasi Seni
          art_installation: {
            is_active: false,
            installation_name: "",
            artist_name: "",
            art_type: "", // "Sculpture" | "Mural" | "Interactive" | "Digital Art" | "Mixed Media"
            installation_description: "",
            dimensions: {
              length: 0,
              width: 0,
              height: 0,
              weight_kg: 0,
            },
            materials_used: [], // ["Metal", "Wood", "Acrylic", "LED", "Fabric"]
            is_interactive: false,
            requires_power: false,
            power_consumption_watt: 0,
            safety_certification: {
              is_certified: false,
              certificate_number: "",
              certificate_date: "",
            },
            insurance: {
              is_insured: false,
              insurance_value: 0,
              insurance_provider: "",
            },
          },

          // Untuk Event Promosi
          promotional_event: {
            is_active: false,
            event_type: "", // "Product Launch" | "Sampling" | "Performance" | "Competition" | "Roadshow"
            event_description: "",
            expected_participants: 0,
            event_activities: [], // ["Product Demo", "Free Sampling", "Games", "Performance", "Meet & Greet"]
            requires_stage: false,
            stage_size: "", // "3x4m" | "4x6m"
            requires_sound_system: false,
            sound_level_limit_db: 85, // Batas kebisingan dalam desibel
            requires_crowd_control: false,
            security_personnel_count: 0,
            promotional_materials: [
              {
                material_type: "", // "Flyer" | "Poster" | "Banner" | "Standee" | "Sample Product"
                quantity: 0,
                distribution_method: "", // "Hand-to-Hand" | "Display" | "Self-Service"
              },
            ],
          },
        },

        // ── Persyaratan Teknis & Keamanan ──────────────────────────────────────
        technical_requirements: {
          structural_load_capacity_kg: 0, // Beban maksimal yang diizinkan
          fire_safety_compliance: false,
          emergency_exit_clearance: true, // Tidak menghalangi jalur evakuasi
          accessibility_compliance: true, // Tidak menghalangi akses difabel
          material_safety_standard: "", // "Non-Flammable" | "Fire Retardant"
          installation_permit_number: "",
          installation_approval_date: "",
        },

        // ── Kewajiban & Tanggung Jawab ──────────────────────────────────────────
        responsibilities: {
          client_responsibilities: [
            // "Setup & Dismantling"
            // "Staff Management"
            // "Cleaning & Maintenance"
            // "Insurance"
            // "Permit & License"
          ],
          mrt_responsibilities: [
            // "Area Provision"
            // "Electricity Supply"
            // "Security Coordination"
            // "Operational Supervision"
          ],
          prohibited_activities: [
            // "Smoking"
            // "Alcohol Distribution"
            // "Blocking Passenger Flow"
            // "Excessive Noise"
          ],
        },

        // ── Biaya & Pembayaran ──────────────────────────────────────────────────
        costs: [
          {
            cost_type: "", // "Area Rental Fee" | "Electricity Fee" | "Setup Fee" | "Security Deposit" | "Cleaning Fee"
            description: "",
            calculation_method: "", // "Per Day" | "Per sqm" | "Flat Rate" | "Per Event"
            unit_price: 0,
            quantity: 0,
            total_amount: 0,
            paymentType: "non-periodic", // atau "periodic"
            periodic: null,
            nonPeriodic: {
              totalInstallment: 1,
              terms: [
                {
                  term: 1,
                  date: "",
                  percentage: 100,
                  amount: 0,
                  notes: "",
                  due_offset_days: 0,
                  revenue_recognition_offset_days: 7,
                },
              ],
            },
            details: {},
          },
        ],

        // ── Deposit & Jaminan ───────────────────────────────────────────────────
        security_deposit: {
          is_required: true,
          amount: 0,
          refund_conditions: "", // Kondisi pengembalian deposit
          refund_timeline_days: 14, // Hari kerja setelah pembongkaran
          deduction_reasons: [
            // "Damage to Property"
            // "Late Dismantling"
            // "Incomplete Cleaning"
          ],
        },

        // ── Dokumentasi & Pelaporan ─────────────────────────────────────────────
        documentation: {
          site_photos_before: [], // URL foto sebelum instalasi
          site_photos_after: [], // URL foto setelah instalasi
          installation_drawings: [], // URL gambar teknis
          event_report: {
            is_required: true,
            submission_deadline_days: 7, // Hari setelah event selesai
            report_format: "PDF", // "PDF" | "Excel" | "Word"
          },
          visitor_count_tracking: {
            is_required: false,
            tracking_method: "", // "Manual Count" | "Digital Counter" | "Survey"
            estimated_daily_visitors: 0,
          },
        },

        // ── Kontak & Koordinasi ─────────────────────────────────────────────────
        coordination: {
          client_pic: {
            name: "",
            position: "",
            phone: "",
            email: "",
          },
          mrt_pic: {
            name: "",
            position: "",
            phone: "",
            email: "",
          },
          emergency_contact: {
            name: "",
            phone: "",
            available_24_7: false,
          },
        },

        // ── Persetujuan & Approval ──────────────────────────────────────────────
        approvals: {
          design_approval: {
            is_approved: false,
            approved_by: "",
            approved_date: "",
            notes: "",
          },
          safety_approval: {
            is_approved: false,
            approved_by: "",
            approved_date: "",
            notes: "",
          },
          operational_approval: {
            is_approved: false,
            approved_by: "",
            approved_date: "",
            notes: "",
          },
        },

        // ── Catatan Tambahan ────────────────────────────────────────────────────
        additional_notes: "",
        special_requirements: "",
        terms_and_conditions: [],
      };
    default:
      // Kembali ke objek kosong jika tipe tidak dikenali
      return {};
  }
};

const getDetailPaymentType = (itemType) => {
  switch (itemType) {
    case "Non Periodic":
      return {
        // Berapa kali total pembayaran dilakukan (1x, 2x, 3x, dst.)
        totalInstallment: 1,
        terms: [
          {
            term: 1, // Urutan ke-berapa (1, 2, 3, ...)
            date: "", // Kapan pembayaran ini dilakukan (DD-MM-YYYY)
            percentage: 100, // Berapa % dari total nilai yang dibayarkan
            amount: 0, // Nominal (opsional, bisa dihitung dari percentage)
            notes: "", // Keterangan tambahan (opsional)
            // ✅ Untuk non-periodic: due_date ada di sini (per termin)
            due_offset_days: 0, // due H+0 dari start
            revenue_recognition_offset_days: 7, // RR H+7 setelah due
            evidence: {
              file: "",
              url: "",
            },
          },
        ],
      };
    case "Periodic":
      return {
        frequency: "daily", // "daily" | "weekly" | "monthly" | "quarterly" | "yearly"

        // Jika frequency === "daily"
        daily: {
          hour: "08", // HH format (00 – 23)
        },

        // Jika frequency === "weekly"
        weekly: {
          day: "1", // 1=Senin, 2=Selasa, ..., 7=Minggu (ISO 8601)
        },

        // Jika frequency === "monthly"
        monthly: {
          date: "01", // DD format (01 – 31)
        },

        // Jika frequency === "quarterly"
        quarterly: {
          month_offset: 1, // bulan ke berapa dalam kuartal: 1 | 2 | 3
          date: "01", // tanggal dalam bulan tersebut (01 – 31)
        },

        // Jika frequency === "yearly"
        yearly: {
          date: "2024-01-01", // DDMMYYYY
        },
        billing_config: {
          billing_offset_days: 0, // tagihan dibuat H+0 dari jadwal
          due_offset_days: 14, // jatuh tempo H+14 dari billing_date
          revenue_recognition_offset_days: 30, // RR H+30 dari billing_date
        },
      };
    default:
      // Kembali ke objek kosong jika tipe tidak dikenali
      return {};
  }
};

const getDetailsCost = (itemType) => {
  switch (itemType) {
    case "Commitment Fee":
      return {
        total: "",
        tipe: "",
        revenue_recognation_date: "",
        due_date: "",
        billing_date: "",
      };
    case "Service Charge":
      return {
        total: "",
        billing_period: "Bulan",
        due_date: "",
        revenue_recognation_date: "",
        billing_date: "",
        // total_price: "",
      };
    case "Cooperation":
      return {};
    case "Rental Fee":
      return {
        total: "",
        billing_period: "Bulan",
        revenue_recognation_date: "",
        due_date: "",
        billing_date: "",
        // total_price: "",
      };
    case "Guarantee Fee":
      return {
        payment_method: "",
        guarantee_value: "",
        guarantee: "",
        terms_of_guarantee: "",
        attachment: {
          file: "",
          url: "",
        },
        status: "",
        fulfillment_date: "",
        billing_date: "",
        due_date: "",
      };
    case "Fit Out Charge":
      return {
        total: "",
      };
    case "Utility Electricity Charge":
      return {
        payment_schema: "",
        cost: "",
        isUsingMinimumBilling: false,
        minimumBilling: "",
        payment_period: "",
        billing_date: "",
        due_date: "",
        units: [],
        utility_record_date: "",
        revenue_recognation_date: "",
      };
    case "Utility Water Charge":
      return {
        payment_schema: "",
        cost: "",
        billing_date: "",
        isUsingMinimumBilling: false,
        minimumBilling: "",
        due_date: "",
        units: [],
        utility_record_date: "",
        revenue_recognation_date: "",
      };
    case "Service Overtime":
      return {
        cost: "",
        total: "",
        quantity: 1,
        billing_date: "",
        due_date: "",
      };
    case "Grant Fee":
      return {
        total: "",
        terms: [
          {
            cost: "",
            billing_date: "",
            status: "",
            fulfillment_date: "",
            period_start: "",
            period_end: "",
            total_period: "",
            due_date: "",
            term_of_grant_fee: "",
            attachment: {
              file: "",
              url: "",
            },
          },
        ],
        yearly_settings: [
          {
            months: [],
            year: "",
            grant_fee: "",
            payment_date: "",
            fulfillment_date: "",
            due_date: "",
            effidence: {
              file: "",
              url: "",
            },
            status: "",
          },
        ],
      };
    case "Security Deposit":
      return {
        total: "",
        type: "",
        revenue_recognation_date: "",
        due_date: "",
        billing_date: "",
        terms: [
          {
            cost: "",
            terms_of_payment: "",
            status: "",
            fulfillment_date: "",
            revenue_recognation_date: "",
            isChopped: false,
            due_date: "",
            billing_date: "",
            terms_of_commitment_fee: "",
            effidence: {
              file: "",
              url: "",
            },
          },
        ],
      };
    case "Tambah Daya":
      return {
        unit: "",
        // ── Parameter daya ──────────────────────────────────────────
        daya_awal: "", // VA — daya terpasang saat ini
        daya_akhir: "", // VA — daya setelah penambahan
        besar_penambahan: "", // VA — computed: daya_akhir - daya_awal

        // ── Harga ───────────────────────────────────────────────────
        harga_per_watt: 2250, // Rp per Watt (default PLN 2.250)
        biaya_penambahan: "", // Rp — computed: besar_penambahan × harga_per_watt
        ppn: "", // Rp — computed: 11% × biaya_penambahan (dibulatkan)
        total_tagihan: "", // Rp — computed: biaya_penambahan + ppn

        // ── Billing ─────────────────────────────────────────────────
        billing_date: "",
        due_date: "",
        status: "",
      };
    default:
      // Kembali ke objek kosong jika tipe tidak dikenali
      return {};
  }
};

const getDetailFinancials = (itemType) => {
  switch (itemType) {
    case "Bagi Hasil":
      return {
        // payment_period: "Bulanan",
        bagi_hasil_method: "flat_rate",

        flat_rate: {
          is_rate_flat: true,
          rate_percent: "",
          sales_amount: 0,
          calculated_amount: "",
        },

        mgp: {
          is_active: false,
          mgp_amount: 0,
          mgp_rate_percent: "",
          mgp_basis: "per_month",
          calculated_mgp_amount: 0,
        },

        yearly_rates: [
          {
            year: 1,
            description: "",
            rate_percent: 0,
          },
        ],

        // Metode: Parameter Target Sales (Conditional Rate)
        // Rate berubah otomatis sesuai pencapaian sales bulanan
        conditional_rate: {
          tiers: [
            {
              sales_target: 0, // Jika sales >= nilai ini
              rate_percent: "", // Gunakan rate % ini
              description: "",
            },
          ],
        },

        // Metode: Skema Nilai Berjenjang (Tiered Scheme)
        // Nilai bagi hasil bertingkat berdasarkan tahun kontrak
        tiered_scheme: {
          tiers: [
            {
              year: 1,
              amount: 0, // Nilai tetap (IDR) untuk tahun ini
              description: "",
            },
          ],
        },

        frequency: "daily", // "daily" | "weekly" | "monthly" | "quarterly" | "yearly"

        // Jika frequency === "daily"
        daily: {
          hour: "08", // HH format (00 – 23)
        },

        // Jika frequency === "weekly"
        weekly: {
          day: "1", // 1=Senin, 2=Selasa, ..., 7=Minggu (ISO 8601)
        },

        // Jika frequency === "monthly"
        monthly: {
          date: "01", // DD format (01 – 31)
        },

        // Jika frequency === "quarterly"
        quarterly: {
          month_offset: 1, // bulan ke berapa dalam kuartal: 1 | 2 | 3
          date: "01", // tanggal dalam bulan tersebut (01 – 31)
        },

        // Jika frequency === "yearly"
        yearly: {
          date: "2024-01-01", // DDMMYYYY
        },
        billing_config: {
          billing_offset_days: 0, // tagihan dibuat H+0 dari jadwal
          due_offset_days: 14, // jatuh tempo H+14 dari billing_date
          revenue_recognition_offset_days: 30, // RR H+30 dari billing_date
          sales_recapitulation_days: 0, // H+0 dari billing_date
        },
      };

    case "Bagi Hasil dengan MGP":
      return {
        scheme_type: "", // Pilihan: "TIERED_RATE" atau "FLAT_MINIMUM_SHARE"
        payment_period: "Tahunan",
        calculated_amount: "",
        scheme: null,
      };
    case "Bukan Sewa":
      return null;
    default:
      // Kembali ke objek kosong jika tipe tidak dikenali
      return {};
  }
};

const getSchemeRevenueShareWithMGP = (itemType) => {
  switch (itemType) {
    // case "TIERED_RATE":
    //   return {
    //     max_revenue_share_amount: 10000000,
    //     actual_sales_amount: 0,
    //     rules: [
    //       {
    //         condition_description: "Jika total penjualan >= Rp 100jt",
    //         sales_amount: 100000000,
    //         // to_sales_amount: null,
    //         rate_percent: 10,
    //       },
    //     ],
    //   };

    // case "FLAT_MINIMUM_SHARE":
    //   return {
    //     max_revenue_share_amount: 10000000,
    //     actual_sales_amount: 0,
    //     rules: [
    //       {
    //         condition_description: "Jika total penjualan >= Rp 100jt",
    //         sales_amount: 100000000,
    //         // to_sales_amount: null,
    //         rate_percent: 10,
    //       },
    //     ],
    //   };
    case "PERIOD":
      return {
        rules: [
          {
            condition_description: "",
            rate_percent: 0,
            period_amount: 0,
          },
        ],
        paymentType: "periodic",
        periodic: {
          frequency: "daily",
          daily: { hour: "08" },
          billing_config: {
            billing_offset_days: 0,
            due_offset_days: 14,
            revenue_recognition_offset_days: 30,
            sales_recapitulation_days: 0,
          },
        },
      };
    default:
      // Kembali ke objek kosong jika tipe tidak dikenali
      return {};
  }
};

const buildPriceOfferRow = () => ({
  charge: "", // e.g. "Rental Fee" | "Service Charge" | ...
  currency: "IDR",
  price: 0,
  frequency: "monthly", // "daily" | "weekly" | "monthly" | "yearly"
  measurement: "sqm", // "sqm" | "unit" | "flat"
});

// Initial state
const initialState = {
  // Get PKS inisial state
  getPKS: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  getPKSById: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  // Create PKS inisial state
  createPKS: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  // Update PKS inisial state
  updatePKS: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  updateStatusPKS: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  // Delete PKS inisial state
  deletePKS: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  // Delete PKS inisial state
  verifyPKSByHead: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  // PKS object value
  pks_object_value: "",

  createPKSInputBody: {
    pks_number: "",
    title: "",
    status: "Draft",
    contract_value: "",
    client: {
      signing_official: {
        name: "",
        title: "",
      },
      mitra: "",
      represented_by: [],
      obligations: [""],
      rights: [""],
      emails: [""],
    },
    provider: {
      // legal_name: "PT. MRT Jakarta",
      mitra: "",
      represented_by: [],
      obligations: [""],
      rights: [""],
      emails: [""],
      signing_official: {
        name: "",
        title: "",
      },
    },
    agreement_object: {
      field: "",
      description: "",
    },
    services: [
      {
        service: "",
        details: "",
      },
    ],
    summary: {
      // isPPNChage: false,
      subtotal: 0,
      discounts: [],
      taxes: [
        {
          tax_name: "PPN",
          rate_percent: 11,
          amount: 0,
        },
      ],
      grand_total: 0,
      currency: "IDR",
    },
    term: {
      option: "",
      period: "",
      duration: "",
      sign_document_date: "",
      payment_terms: "",
      effective_date: null,
      start_date: "",
      isSameEffectiveDate: false,
      end_date: null,
      total_period: "",
      efective_terms: [
        {
          term: "",
          efiden: {
            file: "",
            url: "",
          },
          due_date: "",
          status: "",
          isFinish: false,
        },
      ],
    },

    // Pengaturan notifikasi kontrak/PKS akan berakhir
    expiry_notification: {
      is_enabled: true, // Aktifkan/nonaktifkan notifikasi
      threshold_days: [90, 60, 30, 14, 7], // Kirim notifikasi X hari sebelum berakhir
      recipients: [], // Email penerima notifikasi (default: client & provider emails)
      notification_channels: ["email", "system"], // "email" | "system" | "whatsapp"
      last_notification_sent: null, // Timestamp notifikasi terakhir dikirim
      notification_history: [], // Log riwayat notifikasi yang sudah dikirim
    },

    termination_clause: {
      is_terminable: true,
      notice_period_days: 30,
    },
    legal_details: {
      addendum_clause:
        "Hal-hal yang belum diatur dalam surat perjanjian ini akan diatur kemudian oleh para pihak dalam bentuk addendum yang merupakan bagian yang tidak terpisahkan dari surat perjanjian ini.",
      document_copies: 2,
      is_stamped: true,
    },
    sanctionRules: {
      // Denda jika MRT terlambat/gagal menyediakan layanan infrastruktur
      late_performance: {
        payment_hold: {
          is_active: false,
          description: "",
        },
        penalty: {
          daily_rate_permil: 0, // per mil (‰) per hari
          max_penalty_percentage: 0,
        },
      },
      // Denda jika Bank DKI terlambat melakukan settlement/pembayaran
      late_payment: {
        daily_rate_percentage: 0,
        max_penalty_percentage: 0,
      },
      // Denda khusus downtime sistem Bank DKI melebihi SLA
      sla_breach: {
        is_active: false,
        threshold_hours: 4, // downtime melebihi X jam = denda
        penalty_per_incident: 0,
        max_incidents_per_month: 3,
      },
      // Proses teguran bertahap
      warning_process: [
        { step: 1, days_to_resolve: 7, description: "Surat Peringatan I" },
        { step: 2, days_to_resolve: 14, description: "Surat Peringatan II" },
        {
          step: 3,
          days_to_resolve: 30,
          description: "Surat Peringatan III / Terminasi",
        },
      ],
    },
    legal_document: [],
    pks_document_url: null,
    quotation_origin_id: null,
    isVerifyByFinance: null,
    created_by: null,
  },
  selectedPKS: {
    pks_number: "",
    title: "",
    status: "Draft",
    skema_pendapatan: [],
    contract_value: "",
    costs: [
      {
        cost_type: "",
        description: "",
        details: {},
      },
    ],
    client: {
      signing_official: {
        name: "",
        title: "",
      },
      mitra: "",
      represented_by: {
        name: "",
        title: "",
      },
      obligations: [""],
      rights: [""],
      emails: [""],
    },
    provider: {
      // legal_name: "PT. MRT Jakarta",
      mitra: "",
      represented_by: [""],
      obligations: [""],
      rights: [""],
      emails: [""],
      signing_official: {
        name: "",
        title: "",
      },
    },
    agreement_object: {
      field: "",
      description: "",
    },
    expiry_notification: {
      is_enabled: true, // Aktifkan/nonaktifkan notifikasi
      threshold_days: [90, 60, 30, 14, 7], // Kirim notifikasi X hari sebelum berakhir
      recipients: [], // Email penerima notifikasi (default: client & provider emails)
      notification_channels: ["email", "system"], // "email" | "system" | "whatsapp"
      last_notification_sent: null, // Timestamp notifikasi terakhir dikirim
      notification_history: [], // Log riwayat notifikasi yang sudah dikirim
    },
    services: [
      {
        service: "",
        service_type: "",
        details: "",
      },
    ],
    summary: {
      subtotal: 0,
      discounts: [],
      taxes: [
        {
          tax_name: "PPN",
          rate_percent: 11,
          amount: 0,
        },
      ],
      grand_total: 0,
      currency: "IDR",
    },
    term: {
      period: "",
      duration: "",
      sign_document_date: "",
      payment_terms: "",
      effective_date: null,
      start_date: "",
      isSameEffectiveDate: true,
      end_date: null,
      total: "",
      efective_terms: [
        {
          term: "",
          efiden: {
            file: "",
            url: "",
          },
          due_date: "",
          status: "",
          isFinish: false,
        },
      ],
    },
    financials: {
      revenue_scheme: [], // Ganti ini untuk melihat tampilan yang berbeda
      cost_responsibility: "",
      revenue_share: {
        is_rate_flat: false,
        sales_recapitulation_date: "",
        due_date: "",
        flat_rate: { rate_percent: 0, sales_amount: 0 },
        yearly_rates: [
          {
            description: "",
            rate_percent: 0,
            max_revenue_share_amount: 0,
          },
        ],
      },
      revenue_share_with_mgp: {
        payment_period: "Bulanan",
        term_of_payment: 0,
        sales_recapitulation_date: "",
        yearly_settings: [
          {
            description: "",
            rate: 0,
            mgp: {
              amount: 0,
            },
          },
        ],
      },
      rent_fee_with_revenue_share_mgp: {
        scheme_type: "TIERED_RATE", // Pilihan: "TIERED_RATE" atau "FLAT_MINIMUM_SHARE"
        tiered_rate_scheme: {
          max_revenue_share_amount: 10000000,
          actual_sales_amount: 0,
          payment_period: "Bulanan",
          term_of_payment: 0,
          sales_recapitulation_date: "",
          rules: [
            {
              condition_description: "Jika total penjualan >= Rp 100jt",
              sales_amount: 100000000,
              // to_sales_amount: null,
              rate_percent: 10,
            },
          ],
        },
        flat_minimum_scheme: {
          flat_minimum_revenue_share_amount: 5000000,
          rate_percent: 10,
          payment_period: "Bulanan",
          term_of_payment: 0,
          sales_recapitulation_date: "",
        },
      },
    },
    termination_clause: {
      is_terminable: true,
      notice_period_days: 30,
    },
    legal_details: {
      addendum_clause:
        "Hal-hal yang belum diatur dalam surat perjanjian ini akan diatur kemudian oleh para pihak dalam bentuk addendum yang merupakan bagian yang tidak terpisahkan dari surat perjanjian ini.",
      document_copies: 2,
      is_stamped: true,
    },
    sanctionRules: {
      // Denda jika MRTJ terlambat menyelesaikan pekerjaan
      latePerformance: {
        type: {
          paymentHold: {
            isPaymentHold: false,
            description: "",
          },
          penalty: {
            dailyRatePermil: 0,
            maxPenaltyPercentage: 0,
          },
        },
      },
      // Denda jika BANK DKI terlambat membayar
      latePayment: {
        type: {
          dailyRatePercentage: 0,
          maxPenaltyPercentage: 0,
        },
      },
      // Aturan mengenai proses teguran
      warningProcess: [
        {
          daysToResolve: 0,
          description: "",
        },
      ],
    },
    legal_document: [],
    pks_document_url: null,
    revenue_recognation: null,
    cash_flow: null,
    quotation_origin_id: null,
    verification: null,
  },

  verificationByHeadInputBody: {
    notes: "",
  },
  current_step: 0,
  active_step: 0,
  isFromOffer: false,
  tab_value: 0,
};

export const pksSlice = createSlice({
  name: "pks",
  initialState,
  reducers: {
    setFinancials: (state, action) => {
      const { serviceIndex, propIndex, financials } = action.payload;
      const services = [...state.createPKSInputBody.services];
      services[serviceIndex].details.properties[propIndex].financials = {
        ...services[serviceIndex].details.properties[propIndex].financials,
        ...financials,
      };
      services[serviceIndex].details.properties[propIndex].financials.details =
        getDetailFinancials(financials.revenue_type);
      return {
        ...state,
        createPKSInputBody: { ...state.createPKSInputBody, services },
      };
    },

    setBasePriceSource: (state, action) => {
      const { serviceIndex, propIndex, source } = action.payload;
      const svc =
        state.createPKSInputBody.services[serviceIndex].details.properties[
          propIndex
        ];
      if (svc) {
        svc.base_price_source = source;
        // Reset harga tawar saat sumber berubah agar tidak stale
        svc.price_unit_offer = [];
      }
    },

    /**
     * Simpan snapshot harga dari level tertentu (property/building/floor/unit)
     * dipanggil setelah API return data property/building/floor
     */
    setBasePriceSnapshot: (state, action) => {
      const { serviceIndex, propIndex, level, data } = action.payload;
      // level: "property" | "building" | "floor" | "unit"
      const svc =
        state.createPKSInputBody.services[serviceIndex].details.properties[
          propIndex
        ];
      if (svc) {
        if (!svc.base_price_snapshot) svc.base_price_snapshot = {};
        svc.base_price_snapshot[level] = data;
      }
    },
    /**
     * Update harga per unit secara manual (untuk mode base_price_source === "unit")
     */
    updateUnitPriceSubtotal: (state, action) => {
      const { serviceIndex, propIndex, unitId, priceIndex, field, value } =
        action.payload;
      const svc =
        state.createPKSInputBody.services[serviceIndex].details.properties[
          propIndex
        ];
      if (!svc) return;
      const unit = svc.unit.find((u) => u._id === unitId);
      if (!unit) return;
      if (!unit.price_subtotals) unit.price_subtotals = [];
      if (unit.price_subtotals[priceIndex]) {
        unit.price_subtotals[priceIndex][field] = value;
        // Recalculate subtotal
        if (field === "price" || field === "qty") {
          const p = unit.price_subtotals[priceIndex];
          p.subtotal = (Number(p.price) || 0) * (Number(p.qty) || 0);
        }
      }
    },
    addUnitPriceSubtotal: (state, action) => {
      const { serviceIndex, propIndex, unitId } = action.payload;
      const svc =
        state.createPKSInputBody.services[serviceIndex].details.properties[
          propIndex
        ];
      if (!svc) return;
      const unit = svc.unit.find((u) => u._id === unitId);
      if (!unit) return;
      if (!unit.price_subtotals) unit.price_subtotals = [];
      unit.price_subtotals.push({
        key: "",
        label: "",
        price: 0,
        qty: 0,
        unit: "sqm",
        subtotal: 0,
        currency: "IDR",
        frequency: "monthly",
      });
    },

    removeUnitPriceSubtotal: (state, action) => {
      const { serviceIndex, propIndex, unitId, priceIndex } = action.payload;
      const svc =
        state.createPKSInputBody.services[serviceIndex].details.properties[
          propIndex
        ];
      if (!svc) return;
      const unit = svc.unit.find((u) => u._id === unitId);
      if (!unit || !unit.price_subtotals) return;
      unit.price_subtotals.splice(priceIndex, 1);
    },

    // ── Service: price_unit_offer (harga negosiasi) ────────────────────────
    addPriceOfferRow: (state, action) => {
      const { serviceIndex, propIndex } = action.payload;
      const svc =
        state.createPKSInputBody.services[serviceIndex].details.properties[
          propIndex
        ];
      if (svc) svc.price_unit_offer.push(buildPriceOfferRow());
    },

    removePriceOfferRow: (state, action) => {
      const { serviceIndex, propIndex, rowIndex } = action.payload;
      const svc =
        state.createPKSInputBody.services[serviceIndex].details.properties[
          propIndex
        ];
      if (svc) svc.price_unit_offer.splice(rowIndex, 1);
    },

    updatePriceOfferRow: (state, action) => {
      const { serviceIndex, propIndex, rowIndex, field, value } =
        action.payload;
      const row =
        state.createPKSInputBody.services[serviceIndex].details.properties[
          propIndex
        ]?.price_unit_offer?.[rowIndex];
      if (row) row[field] = value;
    },
    setPeriodicFrequency(state, action) {
      const { serviceIndex, propIndex, costIndex, value } = action.payload;
      const cost =
        state.createPKSInputBody.services[serviceIndex].details.properties[
          propIndex
        ].costs[costIndex];

      cost.periodic.frequency = value;

      if (value === "daily" && !cost.periodic.daily) {
        cost.periodic.daily = { hour: "08" };
      }
      if (value === "weekly" && !cost.periodic.weekly) {
        cost.periodic.weekly = { day: "1" };
      }
      if (value === "monthly" && !cost.periodic.monthly) {
        cost.periodic.monthly = { date: "01" };
      }
      if (value === "quarterly" && !cost.periodic.quarterly) {
        cost.periodic.quarterly = { month_offset: 1, date: "01" };
      }
      if (value === "yearly" && !cost.periodic.yearly) {
        cost.periodic.yearly = { date: "2024-01-01" };
      }
    },
    setQuarterlyConfig(state, action) {
      const { serviceIndex, propIndex, costIndex, field, value } =
        action.payload;
      const q =
        state.createPKSInputBody.services[serviceIndex].details.properties[
          propIndex
        ].costs[costIndex].periodic.quarterly;
      q[field] = value;
    },
    setDailyHour(state, action) {
      const { serviceIndex, propIndex, costIndex, value } = action.payload;
      state.createPKSInputBody.services[serviceIndex].details.properties[
        propIndex
      ].costs[costIndex].periodic.daily.hour = value;
    },
    setWeeklyDay(state, action) {
      const { serviceIndex, propIndex, costIndex, value } = action.payload;
      state.createPKSInputBody.services[serviceIndex].details.properties[
        propIndex
      ].costs[costIndex].periodic.weekly.day = value;
    },
    setMonthlyDate(state, action) {
      const { serviceIndex, propIndex, costIndex, value } = action.payload;
      state.createPKSInputBody.services[serviceIndex].details.properties[
        propIndex
      ].costs[costIndex].periodic.monthly.date = value;
    },
    setYearlyDate(state, action) {
      const { serviceIndex, propIndex, costIndex, value } = action.payload;
      state.createPKSInputBody.services[serviceIndex].details.properties[
        propIndex
      ].costs[costIndex].periodic.yearly.date = value;

      // payload contoh: { date: "15", month: "06", year: "2025" }
    },

    // ── NON-PERIODIK ──────────────────────────────────────────────

    // Update field tertentu pada 1 term (date, percentage, amount, notes)
    addTermPayment(state, action) {
      const { serviceIndex, propIndex, costIndex } = action.payload;

      // state.createPKSInputBody.services[serviceIndex].details.properties[
      //   propIndex
      // ].costs[costIndex].nonPeriodic.terms.push({
      //   term: term,
      //   date: "",
      //   percentage: 0,
      //   amount: 0,
      //   notes: "",
      // });

      const next =
        state.createPKSInputBody.services[serviceIndex].details.properties[
          propIndex
        ].costs[costIndex].nonPeriodic.terms.length + 1;

      state.createPKSInputBody.services[serviceIndex].details.properties[
        propIndex
      ].costs[costIndex].nonPeriodic.terms.push({
        term: next,
        date: "",
        amount: "",
        percentage: 0,
        notes: "",
      });
      state.createPKSInputBody.services[serviceIndex].details.properties[
        propIndex
      ].costs[costIndex].nonPeriodic.totalInstallment = next;
    },

    // Update field tertentu pada 1 term (date, percentage, amount, notes)
    updateTerm(state, action) {
      const { term, field, value, serviceIndex, propIndex, costIndex } =
        action.payload;
      const target = state.createPKSInputBody.services[
        serviceIndex
      ].details.properties[propIndex].costs[costIndex].nonPeriodic.terms.find(
        (t) => t.term === term,
      );

      if (target) {
        target[field] = value;
      }
    },

    /**
     * Distribusi persentase secara merata ke semua term (total = 100%)
     * Tidak butuh payload
     */
    autoDistributeTerms(state, action) {
      const { serviceIndex, propIndex, costIndex } = action.payload;
      const count =
        state.createPKSInputBody.services[serviceIndex].details.properties[
          propIndex
        ].costs[costIndex].nonPeriodic.terms.length;

      if (count === 0) return;

      const pct = parseFloat((100 / count).toFixed(2));

      state.createPKSInputBody.services[serviceIndex].details.properties[
        propIndex
      ].costs[costIndex].nonPeriodic.terms.forEach((t) => {
        t.percentage = pct;
      });
    },

    removeTermPayment(state, action) {
      const { serviceIndex, propIndex, costIndex, termIndex } = action.payload;
      state.createPKSInputBody.services[serviceIndex].details.properties[
        propIndex
      ].costs[costIndex].nonPeriodic.terms.splice(termIndex, 1);

      state.createPKSInputBody.services[serviceIndex].details.properties[
        propIndex
      ].costs[costIndex].nonPeriodic.totalInstallment =
        state.createPKSInputBody.services[serviceIndex].details.properties[
          propIndex
        ].costs[costIndex].nonPeriodic.terms.length;
    },

    setPaymentType(state, action) {
      const { serviceIndex, propIndex, costIndex, value } = action.payload;
      const cost =
        state.createPKSInputBody.services[serviceIndex].details.properties[
          propIndex
        ].costs[costIndex];

      cost.paymentType = value; // "periodic" | "non-periodic"

      if (value === "periodic") {
        cost.periodic = getDetailPaymentType("Periodic");
        delete cost.nonPeriodic;
      } else if (value === "non-periodic") {
        cost.nonPeriodic = getDetailPaymentType("Non Periodic");
        delete cost.periodic;
      }
    },
    setBillingConfig(state, action) {
      const { serviceIndex, propIndex, costIndex, field, value } =
        action.payload;
      const cost =
        state.createPKSInputBody.services[serviceIndex].details.properties[
          propIndex
        ].costs[costIndex];

      if (cost.periodic && cost.periodic.billing_config) {
        cost.periodic.billing_config[field] = value;
      }
    },
    // Aksi untuk 'properties' di dalam details
    addProperty: (state, action) => {
      const { serviceIndex } = action.payload;
      state.createPKSInputBody.services[serviceIndex].details.properties.push({
        // property: "",
        // building: "",
        // floor: "",
        // unit: [],
        property: "",
        building: "",
        floor: "",
        unit: [],
        buildingOptions: [],
        floorOptions: [],
        unitOptions: [],
        costs: [
          // {
          //   cost_type: "",
          //   description: "",
          //   details: {},
          // },
        ],
        financials: {
          revenue_scheme: [], // Ganti ini untuk melihat tampilan yang berbeda
          cost_responsibility: "",
          revenue_type: null,
          details: {},
        },
      });
    },
    removeProperty: (state, action) => {
      const { serviceIndex, propIndex } = action.payload;
      state.createPKSInputBody.services[serviceIndex].details.properties.splice(
        propIndex,
        1,
      );
    },
    updatePropertyField: (state, action) => {
      const { serviceIndex, propIndex, field, value } = action.payload;

      state.createPKSInputBody.services[serviceIndex].details.properties[
        propIndex
      ][field] = value;
    },

    // Reducers untuk array 'taxes'
    addTax: (state) => {
      state.createPKSInputBody.summary.taxes.push({
        tax_name: "",
        rate_percent: 0,
        amount: 0,
      });
    },
    updateTax: (state, action) => {
      const { index, field, value } = action.payload;
      state.createPKSInputBody.summary.taxes[index][field] = value;
    },
    removeTax: (state, action) => {
      state.createPKSInputBody.summary.taxes.splice(action.payload, 1);
    },
    /**
     * Set summary yang sudah dihitung dari calculateSummary() di RetailStation.
     * Payload: { subtotal, taxes, grandTotal, service_summaries? }
     */
    setComputedSummary: (state, action) => {
      const { subtotal, taxes, grandTotal, service_summaries } = action.payload;
      const s = state.createPKSInputBody.summary;
      s.subtotal = subtotal;
      s.grand_total = grandTotal;
      // Perbarui amount di setiap tax tanpa menghapus entry dari user
      if (Array.isArray(taxes) && taxes.length > 0) {
        s.taxes = taxes.map((incoming) => ({
          tax_name: incoming.tax_name,
          rate_percent: incoming.rate_percent,
          amount: incoming.amount,
        }));
      }
      // Simpan breakdown per service agar DetailPKS bisa menampilkannya
      if (service_summaries) {
        s.service_summaries = service_summaries;
      }
    },
    setIsFromOffer: (state, action) => {
      return {
        ...state,
        isFromOffer: action.payload,
      };
    },

    addCostItem: (state, action) => {
      const { serviceIndex, propIndex } = action.payload;
      if (
        state.createPKSInputBody.services[serviceIndex] &&
        state.createPKSInputBody.services[serviceIndex].details &&
        state.createPKSInputBody.services[serviceIndex].details.properties[
          propIndex
        ]
      ) {
        if (
          !state.createPKSInputBody.services[serviceIndex].details.properties[
            propIndex
          ].costs
        ) {
          state.createPKSInputBody.services[serviceIndex].details.properties[
            propIndex
          ].costs = [];
        }
        state.createPKSInputBody.services[serviceIndex].details.properties[
          propIndex
        ].costs.push({
          description: "",
          cost_type: "",
          paymentType: "periodic", // "periodic" | "non-periodic"

          periodic: {
            frequency: "daily", // "daily" | "weekly" | "monthly" | "yearly"

            // Jika frequency === "daily"
            // Jam berapa pembayaran dilakukan setiap harinya
            daily: {
              hour: "08", // HH format (00 – 23)
            },

            // Jika frequency === "weekly"
            // Hari apa dalam seminggu pembayaran dilakukan
            weekly: {
              day: "1", // 1=Senin, 2=Selasa, ..., 7=Minggu (ISO 8601)
            },

            // Jika frequency === "monthly"
            // Tanggal berapa setiap bulannya pembayaran dilakukan
            monthly: {
              date: "01", // DD format (01 – 31)
            },

            // Jika frequency === "yearly"
            // Tanggal, bulan, dan tahun pembayaran tahunan
            yearly: {
              date: "2024-01-01", // DDMMYYYY
            },
          },

          // ── NON-PERIODIK ────────────────────────────────────────────────
          // Pembayaran berdasarkan jumlah kali (interval), bukan periode waktu
          nonPeriodic: {
            // Berapa kali total pembayaran dilakukan (1x, 2x, 3x, dst.)
            totalInstallment: 1,
            terms: [
              {
                term: 1, // Urutan ke-berapa (1, 2, 3, ...)
                date: "", // Kapan pembayaran ini dilakukan (DD-MM-YYYY)
                percentage: 100, // Berapa % dari total nilai yang dibayarkan
                amount: 0, // Nominal (opsional, bisa dihitung dari percentage)
                notes: "", // Keterangan tambahan (opsional)
              },
            ],
          },
          details: {},
        });
      }
    },
    removeCostItem: (state, action) => {
      const { serviceIndex, propIndex, costIndex } = action.payload;
      if (
        state.createPKSInputBody.services[serviceIndex] &&
        state.createPKSInputBody.services[serviceIndex].details &&
        state.createPKSInputBody.services[serviceIndex].details.properties[
          propIndex
        ] &&
        state.createPKSInputBody.services[serviceIndex].details.properties[
          propIndex
        ].costs
      ) {
        state.createPKSInputBody.services[serviceIndex].details.properties[
          propIndex
        ].costs.splice(costIndex, 1);
      }
    },

    // --- Reducers untuk array 'terms' di dalam item biaya ---
    addTerm: (state, action) => {
      const { serviceIndex, propIndex, costIndex } = action.payload;
      state.createPKSInputBody.services[serviceIndex].details.properties[
        propIndex
      ].costs[costIndex].details.terms.push({
        cost: "",
        terms_of_payment: "",
        status: "Pending",
        fulfillment_date: null,
        due_date: null,
        effidence: { file: null, url: "" },
      });
    },
    // --- Reducers untuk array 'terms' di dalam item biaya ---
    addTermCommitmentFee: (state, action) => {
      const { serviceIndex, propIndex, costIndex } = action.payload;
      state.createPKSInputBody.services[serviceIndex].details.properties[
        propIndex
      ].costs[costIndex].details.terms.push({
        cost: "",
        terms_of_payment: "",
        status: "",
        fulfillment_date: "",
        due_date: "",
        billing_date: "",
        terms_of_commitment_fee: "",
        effidence: {
          file: "",
          url: "",
        },
      });
    },
    removeTerm: (state, action) => {
      const { serviceIndex, propIndex, costIndex, index } = action.payload;
      state.createPKSInputBody.services[serviceIndex].details.properties[
        propIndex
      ].costs[costIndex].details.terms.splice(index, 1);
    },
    updateTermField: (state, action) => {
      const { serviceIndex, propIndex, costIndex, index, field, value } =
        action.payload;

      state.createPKSInputBody.services[serviceIndex].details.properties[
        propIndex
      ].costs[costIndex].details.terms[index][field] = value;
    },

    // Action ini bisa dipanggil dari onChange DatePicker Anda
    updateCooperationDates(state, action) {
      const { serviceIndex, propIndex, itemIndex } = action.payload;

      // 2. Panggil fungsi utilitas untuk membuat template yearly_settings
      const newYearlySettings = getYearlySettingsTemplate(
        state.createPKSInputBody.term.start_date,
        state.createPKSInputBody.term.end_date,
      );

      // 3. Update state 'costs' dengan data baru
      const service = state.createPKSInputBody.services[serviceIndex];
      if (
        service &&
        service.details?.properties?.[propIndex]?.costs?.length > 0
      ) {
        service.details.properties[propIndex].costs[
          itemIndex
        ].details.yearly_settings = newYearlySettings;
      }
    },

    // Reducer generik untuk mengubah field (termasuk yang nested)
    setSanctionField: (state, action) => {
      const { path, value } = action.payload;
      const keys = path.split(".");
      let current = state.createPKSInputBody.sanctionRules;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
    },

    // --- Reducers untuk array 'warningProcess' ---
    addWarningProcess: (state) => {
      const wp = state.createPKSInputBody.sanctionRules.warning_process;
      wp.push({
        step: wp.length + 1,
        days_to_resolve: 0,
        description: "",
      });
    },
    updateWarningProcess: (state, action) => {
      const { index, field, value } = action.payload;
      state.createPKSInputBody.sanctionRules.warning_process[index][field] =
        value;
    },
    removeWarningProcess: (state, action) => {
      const { index } = action.payload;
      state.createPKSInputBody.sanctionRules.warning_process.splice(index, 1);
      // Reassign steps after deletion to keep sequence tidy
      state.createPKSInputBody.sanctionRules.warning_process.forEach(
        (item, i) => {
          item.step = i + 1;
        },
      );
    },

    // --- Reducers untuk array 'efective_terms' ---
    addEfectiveTerm: (state) => {
      state.createPKSInputBody.term.efective_terms.push({
        term: "",
        efiden: "",
        due_date: null,
      });
    },
    updateEfectiveTerm: (state, action) => {
      const { index, field, value } = action.payload;
      state.createPKSInputBody.term.efective_terms[index][field] = value;
    },
    removeEfectiveTerm: (state, action) => {
      const { index } = action.payload;
      state.createPKSInputBody.term.efective_terms.splice(index, 1);
    },

    // TAMBAHKAN REDUCER BARU INI
    updatePKSSummary: (state, action) => {
      const { subtotal, ppnAmount } = action.payload;
      const summary = state.createPKSInputBody.summary;

      summary.subtotal = subtotal;
      summary.taxes = [
        {
          tax_name: "PPN",
          rate_percent: 11,
          amount: ppnAmount,
        },
      ];
      summary.grand_total = subtotal + ppnAmount;
    },

    // Action untuk mengubah revenue type pada properti spesifik
    updatePropertyFinancialsType: (state, action) => {
      const { serviceIndex, propIndex, newType } = action.payload;
      const property = state.createPKSInputBody.services[serviceIndex]?.details;

      if (property && property.financials) {
        property.financials.revenue_type = newType;
        property.financials.details = getDetailFinancials(newType);
      }
    },

    // Action untuk mengubah scheme type pada properti spesifik (MGP)
    updatePropertyFinancialsSchemeType: (state, action) => {
      const { serviceIndex, propIndex, newType } = action.payload;
      const property =
        state.createPKSInputBody.services[serviceIndex]?.details?.properties?.[
          propIndex
        ];

      if (property && property.financials && property.financials.details) {
        property.financials.details.scheme_type = newType;
        property.financials.details.scheme =
          getSchemeRevenueShareWithMGP(newType);

        if (
          newType === "PERIOD" &&
          property.start_date &&
          property.end_property_date
        ) {
          const startDate = dayjs(property.start_date);
          const endDate = dayjs(property.end_property_date);

          let numYears = Math.ceil(endDate.diff(startDate, "month", true) / 12);
          if (numYears <= 0 || isNaN(numYears)) numYears = 1;

          const ordinalNames = [
            "Pertama",
            "Kedua",
            "Ketiga",
            "Keempat",
            "Kelima",
            "Keenam",
            "Ketujuh",
            "Kedelapan",
            "Kesembilan",
            "Kesepuluh",
          ];

          const startYear = startDate.year();

          const generatedRules = [];
          for (let i = 0; i < numYears; i++) {
            const yearName = ordinalNames[i] || `ke-${i + 1}`;
            generatedRules.push({
              condition_description: `Tahun ${yearName}`,
              rate_percent: 0,
              period_amount: 0,
              rate_period: startYear + i,
            });
          }

          property.financials.details.scheme.rules = generatedRules;
        }
      }
    },

    // Action untuk mengubah field financials pada properti spesifik
    updatePropertyFinancialsField: (state, action) => {
      const { serviceIndex, propIndex, path, value } = action.payload;
      const property =
        state.createPKSInputBody.services[serviceIndex]?.details?.properties?.[
          propIndex
        ];

      if (property && property.financials) {
        // Handle nested path seperti "details.payment_period" atau "details.scheme.rate_percent"
        const keys = path.split(".");
        let current = property.financials;

        for (let i = 0; i < keys.length - 1; i++) {
          if (!current[keys[i]]) {
            // Jika path tidak ada, buat objek kosong (safety check)
            current[keys[i]] = {};
          }
          current = current[keys[i]];
        }

        current[keys[keys.length - 1]] = value;
      }
    },

    // Action untuk mengubah revenue type di level service (bukan di dalam properties)
    updateServiceFinancialsType: (state, action) => {
      const { serviceIndex, newType } = action.payload;
      const details = state.createPKSInputBody.services[serviceIndex]?.details;
      if (details && details.financials) {
        details.financials.revenue_type = newType;
        details.financials.details = getDetailFinancials(newType);
      }
    },

    // Action untuk mengubah field financials di level service (bukan di dalam properties)
    updateServiceFinancialsField: (state, action) => {
      const { serviceIndex, path, value } = action.payload;
      const details = state.createPKSInputBody.services[serviceIndex]?.details;
      if (details && details.financials) {
        const keys = path.split(".");
        let current = details.financials;
        for (let i = 0; i < keys.length - 1; i++) {
          if (!current[keys[i]]) current[keys[i]] = {};
          current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = value;
      }
    },

    // Action untuk menambah MGP yearly setting pada properti
    addPropertyMgpYearlySetting: (state, action) => {
      const { serviceIndex, propIndex } = action.payload;
      const property =
        state.createPKSInputBody.services[serviceIndex]?.details?.properties?.[
          propIndex
        ];

      if (
        property?.financials?.details?.scheme?.rules &&
        Array.isArray(property.financials.details.scheme.rules)
      ) {
        property.financials.details.scheme.rules.push({
          condition_description: "",
          rate_percent: 0,
          period_amount: 0,
        });
      }
    },

    // Action untuk menghapus MGP yearly setting pada properti
    removePropertyMgpYearlySetting: (state, action) => {
      const { serviceIndex, propIndex, ruleIndex } = action.payload;
      const property =
        state.createPKSInputBody.services[serviceIndex]?.details?.properties?.[
          propIndex
        ];

      if (
        property?.financials?.details?.scheme?.rules &&
        Array.isArray(property.financials.details.scheme.rules)
      ) {
        property.financials.details.scheme.rules.splice(ruleIndex, 1);
      }
    },

    // Action untuk menambah Yearly Rate pada properti (Revenue Share biasa)
    addPropertyYearlyRate: (state, action) => {
      const { serviceIndex, propIndex } = action.payload;
      const property =
        state.createPKSInputBody.services[serviceIndex]?.details?.properties?.[
          propIndex
        ];

      if (
        property?.financials?.details?.yearly_rates &&
        Array.isArray(property.financials.details.yearly_rates)
      ) {
        property.financials.details.yearly_rates.push({
          description: "",
          rate_percent: 0,
          max_revenue_share_amount: 0,
        });
      }
    },

    // Action untuk menghapus Yearly Rate pada properti
    removePropertyYearlyRate: (state, action) => {
      const { serviceIndex, propIndex, rateIndex } = action.payload;
      const property =
        state.createPKSInputBody.services[serviceIndex]?.details?.properties?.[
          propIndex
        ];

      if (
        property?.financials?.details?.yearly_rates &&
        Array.isArray(property.financials.details.yearly_rates)
      ) {
        property.financials.details.yearly_rates.splice(rateIndex, 1);
      }
    },

    // INI BAGIAN PENTINGNYA: Action untuk mengubah tipe item
    updateItemType: (state, action) => {
      // Payload harus berisi index item, serviceId (ObjectId), dan tipe barunya
      const { itemIndex, serviceId, newType, snapshotProperties } =
        action.payload;

      // Ambil item yang akan diubah
      const itemToUpdate = state.createPKSInputBody.services[itemIndex];

      if (itemToUpdate) {
        const existingBuilding = itemToUpdate.details?.building;
        // Simpan service (ObjectId) untuk referensi backend & verifikasi work_unit
        itemToUpdate.service = serviceId || "";
        itemToUpdate.service_type = newType;

        // Ganti keseluruhan objek 'details' dengan struktur baru
        itemToUpdate.details = getDetailsStructure(newType);

        if (
          snapshotProperties &&
          Array.isArray(snapshotProperties) &&
          itemToUpdate.details.properties &&
          state.isFromOffer
        ) {
          const baseProperty = itemToUpdate.details.properties[0] || {};
          const calculateCostTotal = (
            costType,
            frequency,
            startDate,
            endDate,
            units,
          ) => {
            if (!startDate || !endDate || !units || units.length === 0)
              return 0;
            const sDate = dayjs(startDate);
            const eDate = dayjs(endDate);
            if (!sDate.isValid() || !eDate.isValid()) return 0;
            // Map cost type to key in price_subtotals
            const COST_KEY_MAP = {
              "Rental Fee": "rental_fee",
              "Service Charge": "service_charge",
              "Utility Electricity Charge": "electricity",
              "Utility Water Charge": "water",
            };
            const costKey = COST_KEY_MAP[costType];
            // Sum up subtotals from all units
            let totalSubtotalPerPeriod = 0;
            units.forEach((u) => {
              const hasUnitPriceList =
                Array.isArray(u.unitPriceList) && u.unitPriceList.length > 0;
              const priceList = hasUnitPriceList
                ? u.unitPriceList
                : u?.price_subtotals;
              const s = priceList?.find((p) => p.key === costKey);
              if (s) {
                totalSubtotalPerPeriod += Number(s.subtotal) || 0;
              }
            });
            if (frequency === "daily") {
              const days = eDate.diff(sDate, "day");
              return totalSubtotalPerPeriod * Math.ceil(days);
            } else if (frequency === "weekly") {
              const weeks = eDate.diff(sDate, "week");
              return totalSubtotalPerPeriod * Math.ceil(weeks);
            } else if (frequency === "monthly") {
              const months = Math.ceil(eDate.diff(sDate, "month", true));
              return totalSubtotalPerPeriod * months;
            } else if (frequency === "yearly") {
              const years = Math.ceil(eDate.diff(sDate, "year", true));
              return totalSubtotalPerPeriod * years;
            }
            return 0;
          };

          itemToUpdate.details.properties = snapshotProperties.map((sp) => {
            const spUnits = sp.unit || [];

            // ── Derive price_subtotals per unit dari snapshot ──────────────
            const snapshotLevel = sp.base_price_source || "";
            const snapshotData = sp.base_price_snapshot || {};

            /**
             * Konversi items dari base_price snapshot menjadi array price_subtotals.
             * Setiap item di snapshot: { charge, price, qty, measurement, frequency, ... }
             * Mapping ke key yang dipakai di calculateCostTotal:
             *   "Rental Fee"                  → "rental_fee"
             *   "Service Charge"              → "service_charge"
             *   "Utility Electricity Charge"  → "electricity"
             *   "Utility Water Charge"        → "water"
             */
            const CHARGE_TO_KEY = {
              "Rental Fee": "rental_fee",
              "Service Charge": "service_charge",
              "Utility Electricity Charge": "electricity",
              "Utility Water Charge": "water",
            };

            const itemsToPriceSubtotals = (items = []) =>
              items.map((item) => ({
                key: CHARGE_TO_KEY[item.charge] || item.charge,
                label: item.charge,
                subtotal: Number(item.price) * Number(item.qty || 1),
                price: Number(item.price),
                qty: Number(item.qty || 1),
                measurement: item.measurement,
                frequency: item.frequency,
              }));

            // Enrich units dengan price_subtotals dari snapshot TERLEBIH DAHULU
            // agar calculateCostTotal menggunakan harga terbaru
            const enrichedUnits = spUnits.map((unit) => {
              let priceSubtotals;

              if (snapshotLevel === "unit") {
                // Level Unit: setiap unit punya entry sendiri di snapshot
                const snapshotUnitItems = snapshotData.unit?.items || [];
                const matched = snapshotUnitItems.find(
                  (item) =>
                    (item.unit?._id || item.unit) === (unit._id || unit),
                );
                priceSubtotals = matched
                  ? itemsToPriceSubtotals(matched.price_subtotals || [matched])
                  : unit.price_subtotals || [];
              } else if (
                snapshotLevel === "property" ||
                snapshotLevel === "building" ||
                snapshotLevel === "floor"
              ) {
                // Level Property / Building / Floor: semua unit pakai harga yang sama
                const sharedItems = snapshotData[snapshotLevel]?.items || [];
                priceSubtotals = itemsToPriceSubtotals(sharedItems);
              } else {
                // Tidak ada snapshot terpilih — pertahankan price_subtotals lama
                priceSubtotals = unit.price_subtotals || [];
              }

              return { ...unit, price_subtotals: priceSubtotals };
            });

            // Hitung cost total menggunakan enrichedUnits (harga dari snapshot)
            const rentalTotal = calculateCostTotal(
              "Rental Fee",
              "daily",
              sp.start_date,
              sp.end_date,
              enrichedUnits,
            );
            const serviceChargeTotal = calculateCostTotal(
              "Service Charge",
              "daily",
              sp.start_date,
              sp.end_date,
              enrichedUnits,
            );

            return {
              ...baseProperty,
              base_price_source: snapshotLevel,
              base_price_snapshot: sp.base_price_snapshot,
              property: sp.property?._id || sp.property || "",
              building: sp.building?._id || sp.building || "",
              buildingOptions: sp.building ? [sp.building] : [],
              floor: sp.floor?._id || sp.floor || "",
              floorOptions: sp.floor ? [sp.floor] : [],
              unit: enrichedUnits,
              unitOptions: enrichedUnits,
              fitting_in: sp.fitting_out_date || "",
              fitting_out: sp.fitting_out_date || "",
              handover_date: sp.handover_date || "",
              start_date: sp.start_date || "",
              end_property_date: sp.end_date || "",
              base_price: sp.base_price || "",
              costs: [
                {
                  cost_type: "Rental Fee",
                  evidence: "",
                  paymentType: "periodic",
                  periodic: {
                    frequency: "daily",
                    daily: { hour: "08" },
                    billing_config: {
                      billing_offset_days: 0,
                      due_offset_days: 14,
                      revenue_recognition_offset_days: 30,
                    },
                  },
                  nonPeriodic: null,
                  details: { total: rentalTotal },
                },
                {
                  cost_type: "Service Charge",
                  evidence: "",
                  paymentType: "periodic",
                  periodic: {
                    frequency: "daily",
                    daily: { hour: "08" },
                    billing_config: {
                      billing_offset_days: 0,
                      due_offset_days: 14,
                      revenue_recognition_offset_days: 30,
                    },
                  },
                  nonPeriodic: null,
                  details: { total: serviceChargeTotal },
                },
              ],
              price_unit_offer: sp.price_unit_offer || [],
            };
          });
        }

        // Remove any properties that shouldn't be at the top level
        const detailsKeys = Object.keys(getDetailsStructure(newType));
        detailsKeys.forEach((key) => {
          if (itemToUpdate.hasOwnProperty(key)) {
            delete itemToUpdate[key];
          }
        });

        // Jika ada building yang tersimpan dan tipe baru memerlukan building, kembalikan nilainya
        if (
          existingBuilding &&
          itemToUpdate.details.hasOwnProperty("building")
        ) {
          itemToUpdate.details.building = existingBuilding;
        }
      }
    },

    updateItem: (state, action) => {
      const { index, field, value } = action.payload;
      const items = state.createPKSInputBody.costs;

      // 1. Update nilai field pada item yang spesifik
      if (items[index]) {
        items[index][field] = value;
      }

      // 2. Hitung ulang total_price untuk item tersebut
      // Selalu pastikan quantity dan unit_price adalah angka
      const quantity = Number(items[index].quantity) || 0;
      const unitPrice = Number(items[index].unit_price) || 0;
      items[index].total_price = quantity * unitPrice;

      // 3. Hitung ulang summary (subtotal dan PPN) dari awal
      const subtotal = items.reduce(
        (sum, item) => sum + (Number(item.total_price) || 0),
        0,
      );
      const ppnAmount = subtotal * 0.11;

      const summary = state.createPKSInputBody.summary;

      summary.subtotal = subtotal;
      summary.taxes = [
        {
          tax_name: "PPN",
          rate_percent: 11,
          amount: ppnAmount,
        },
      ];
      summary.grand_total = subtotal + ppnAmount;
    },

    // Action untuk mengubah nilai di dalam 'details'
    updateItemDetails: (state, action) => {
      const { itemIndex, field, value } = action.payload;

      const itemToUpdate = state.createPKSInputBody.services[itemIndex];

      if (itemToUpdate && itemToUpdate.details) {
        itemToUpdate.details[field] = value;
      }
    },

    updateNamingRightsSection: (state, action) => {
      const { itemIndex, section, field, value } = action.payload;
      const item = state.createPKSInputBody.services[itemIndex];
      if (item?.details?.[section] !== undefined) {
        item.details[section][field] = value;
      }
    },

    updateNamingRightsHakPenamaan: (state, action) => {
      const { itemIndex, field, value } = action.payload;
      const item = state.createPKSInputBody.services[itemIndex];
      if (item?.details?.hak_penamaan !== undefined) {
        item.details.hak_penamaan[field] = value;
      }
    },

    // ───────────────────────────────────────────────────────────────────────

    // Action untuk mengubah nilai di dalam 'details'
    updateCostItemDetails: (state, action) => {
      const { serviceIndex, propIndex, itemIndex, field, value } =
        action.payload;

      const service = state.createPKSInputBody.services[serviceIndex];

      // Ensure the item exists before trying to update it
      if (
        service &&
        service.details?.properties?.[propIndex]?.costs?.[itemIndex]
      ) {
        service.details.properties[propIndex].costs[itemIndex].details[field] =
          value;
      }

      // Recalculate the subtotal from the updated costs array.
      // This approach correctly uses the draft state provided by Immer.
      const subtotal = service.details.properties.reduce((propSum, prop) => {
        const propCostSum =
          prop.costs?.reduce((sum, item) => {
            return sum + (Number(item.details?.total) || 0);
          }, 0) || 0;
        return propSum + propCostSum;
      }, 0);

      const ppnAmount = subtotal * 0.11; // 11% PPN

      // Update the summary object
      state.createPKSInputBody.summary.subtotal = subtotal;
      state.createPKSInputBody.summary.taxes = [
        {
          tax_name: "PPN",
          rate_percent: 11,
          amount: ppnAmount,
        },
      ];
      state.createPKSInputBody.summary.grand_total = subtotal + ppnAmount;
    },

    /**
     * Batch-update multiple detail fields of a cost item in ONE dispatch.
     * Prevents N re-renders when N fields need to change simultaneously
     * (e.g. syncCalc for "Tambah Daya" updates 5 computed fields at once).
     *
     * payload: { serviceIndex, propIndex, itemIndex, patch: { [field]: value, ... } }
     */
    updateCostItemDetailsBatch: (state, action) => {
      const { serviceIndex, propIndex, itemIndex, patch } = action.payload;

      const service = state.createPKSInputBody.services[serviceIndex];
      const costItem =
        service?.details?.properties?.[propIndex]?.costs?.[itemIndex];

      if (!costItem) return;

      // Apply every field from the patch in the same Immer draft mutation
      Object.entries(patch).forEach(([field, value]) => {
        costItem.details[field] = value;
      });

      // Recalculate summary once after all fields are patched
      const subtotal = service.details.properties.reduce((propSum, prop) => {
        const propCostSum =
          prop.costs?.reduce((sum, item) => {
            return sum + (Number(item.details?.total) || 0);
          }, 0) || 0;
        return propSum + propCostSum;
      }, 0);

      const ppnAmount = subtotal * 0.11;
      state.createPKSInputBody.summary.subtotal = subtotal;
      state.createPKSInputBody.summary.taxes = [
        { tax_name: "PPN", rate_percent: 11, amount: ppnAmount },
      ];
      state.createPKSInputBody.summary.grand_total = subtotal + ppnAmount;
    },

    addNamingRightsCost: (state, action) => {
      const { itemIndex } = action.payload;
      const item = state.createPKSInputBody.services[itemIndex];
      if (item?.details?.costs !== undefined) {
        item.details.costs.push({
          description: "",
          cost_type: "",
          paymentType: "periodic", // "periodic" | "non-periodic"

          periodic: {
            frequency: "daily", // "daily" | "weekly" | "monthly" | "yearly"

            // Jika frequency === "daily"
            // Jam berapa pembayaran dilakukan setiap harinya
            daily: {
              hour: "08", // HH format (00 – 23)
            },

            // Jika frequency === "weekly"
            // Hari apa dalam seminggu pembayaran dilakukan
            weekly: {
              day: "1", // 1=Senin, 2=Selasa, ..., 7=Minggu (ISO 8601)
            },

            // Jika frequency === "monthly"
            // Tanggal berapa setiap bulannya pembayaran dilakukan
            monthly: {
              date: "01", // DD format (01 – 31)
            },

            // Jika frequency === "yearly"
            // Tanggal, bulan, dan tahun pembayaran tahunan
            yearly: {
              date: "2024-01-01", // DDMMYYYY
            },
          },

          // ── NON-PERIODIK ────────────────────────────────────────────────
          // Pembayaran berdasarkan jumlah kali (interval), bukan periode waktu
          nonPeriodic: {
            // Berapa kali total pembayaran dilakukan (1x, 2x, 3x, dst.)
            totalInstallment: 1,
            terms: [
              {
                term: 1, // Urutan ke-berapa (1, 2, 3, ...)
                date: "", // Kapan pembayaran ini dilakukan (DD-MM-YYYY)
                percentage: 100, // Berapa % dari total nilai yang dibayarkan
                amount: 0, // Nominal (opsional, bisa dihitung dari percentage)
                notes: "", // Keterangan tambahan (opsional)
                // ✅ Untuk non-periodic: due_date ada di sini (per termin)
                due_offset_days: 0, // due H+0 dari start
                revenue_recognition_offset_days: 7, // RR H+7 setelah due
              },
            ],
          },
          details: {},
        });
      }

      item.details.costs[item.details.costs.length - 1].details =
        getDetailsCost();
    },

    removeNamingRightsCost: (state, action) => {
      const { itemIndex, costIndex } = action.payload;
      const item = state.createPKSInputBody.services[itemIndex];
      if (item?.details?.costs !== undefined) {
        item.details.costs.splice(costIndex, 1);
      }
    },

    updateNamingRightsCostItem: (state, action) => {
      const { itemIndex, costIndex, field, value } = action.payload;
      const item = state.createPKSInputBody.services[itemIndex];
      if (item?.details?.costs?.[costIndex] !== undefined) {
        item.details.costs[costIndex][field] = value;
      }
    },

    setNRPaymentType: (state, action) => {
      const { itemIndex, costIndex, value } = action.payload;
      const cost =
        state.createPKSInputBody.services[itemIndex]?.details?.costs?.[
          costIndex
        ];
      if (!cost) return;
      cost.paymentType = value;
      if (value === "periodic") {
        cost.periodic = {
          frequency: "monthly",
          daily: { hour: "08" },
          weekly: { day: "1" },
          monthly: { date: "01" },
          yearly: { date: "" },
        };
        delete cost.nonPeriodic;
      } else {
        cost.nonPeriodic = {
          totalInstallment: 1,
          terms: [{ term: 1, date: "", percentage: 100, amount: 0, notes: "" }],
        };
        delete cost.periodic;
      }
    },

    setNRPeriodicFrequency: (state, action) => {
      const { itemIndex, costIndex, value } = action.payload;
      const cost =
        state.createPKSInputBody.services[itemIndex]?.details?.costs?.[
          costIndex
        ];
      if (cost?.periodic) cost.periodic.frequency = value;
    },

    setNRDailyHour: (state, action) => {
      const { itemIndex, costIndex, value } = action.payload;
      const cost =
        state.createPKSInputBody.services[itemIndex]?.details?.costs?.[
          costIndex
        ];
      if (cost?.periodic?.daily) cost.periodic.daily.hour = value;
    },

    /** Payload: { itemIndex, costIndex, value: "1"–"7" } */
    setNRWeeklyDay: (state, action) => {
      const { itemIndex, costIndex, value } = action.payload;
      const cost =
        state.createPKSInputBody.services[itemIndex]?.details?.costs?.[
          costIndex
        ];
      if (cost?.periodic?.weekly) cost.periodic.weekly.day = value;
    },

    /** Payload: { itemIndex, costIndex, value: "DD" } */
    setNRMonthlyDate: (state, action) => {
      const { itemIndex, costIndex, value } = action.payload;
      const cost =
        state.createPKSInputBody.services[itemIndex]?.details?.costs?.[
          costIndex
        ];
      if (cost?.periodic?.monthly) cost.periodic.monthly.date = value;
    },

    /** Payload: { itemIndex, costIndex, value: "YYYY-MM-DD" } */
    setNRYearlyDate: (state, action) => {
      const { itemIndex, costIndex, value } = action.payload;
      const cost =
        state.createPKSInputBody.services[itemIndex]?.details?.costs?.[
          costIndex
        ];
      if (cost?.periodic?.yearly) cost.periodic.yearly.date = value;
    },

    /** Tambah satu term ke nonPeriodic.terms. Payload: { itemIndex, costIndex } */
    addNRNonPeriodicTerm: (state, action) => {
      const { itemIndex, costIndex } = action.payload;
      const cost =
        state.createPKSInputBody.services[itemIndex]?.details?.costs?.[
          costIndex
        ];
      if (cost?.nonPeriodic?.terms) {
        const nextTerm = cost.nonPeriodic.terms.length + 1;
        cost.nonPeriodic.terms.push({
          term: nextTerm,
          date: "",
          percentage: 0,
          amount: 0,
          notes: "",
        });
        cost.nonPeriodic.totalInstallment = nextTerm;
      }
    },

    /** Hapus satu term. Payload: { itemIndex, costIndex, termIndex } */
    removeNRNonPeriodicTerm: (state, action) => {
      const { itemIndex, costIndex, termIndex } = action.payload;
      const cost =
        state.createPKSInputBody.services[itemIndex]?.details?.costs?.[
          costIndex
        ];
      if (cost?.nonPeriodic?.terms) {
        cost.nonPeriodic.terms.splice(termIndex, 1);
        cost.nonPeriodic.totalInstallment = cost.nonPeriodic.terms.length;
      }
    },

    /** Update satu field di dalam term. Payload: { itemIndex, costIndex, termIndex, field, value } */
    updateNRNonPeriodicTerm: (state, action) => {
      const { itemIndex, costIndex, termIndex, field, value } = action.payload;
      const cost =
        state.createPKSInputBody.services[itemIndex]?.details?.costs?.[
          costIndex
        ];
      if (cost?.nonPeriodic?.terms?.[termIndex] !== undefined) {
        cost.nonPeriodic.terms[termIndex][field] = value;
      }
    },

    // ───────────────────────────────────────────────────────────────────────

    updateCostItem: (state, action) => {
      const { serviceIndex, propIndex, index, field, value } = action.payload;
      const service = state.createPKSInputBody.services[serviceIndex];
      const items = service?.details?.properties?.[propIndex]?.costs;

      // 1. Update nilai field pada item yang spesifik
      if (items && items[index]) {
        items[index][field] = value;
      }

      // 2. Hitung ulang total_price untuk item tersebut
      // Selalu pastikan quantity dan unit_price adalah angka
      const quantity = Number(items[index].quantity) || 0;
      const unitPrice = Number(items[index].unit_price) || 0;
      items[index].total_price = quantity * unitPrice;

      // 3. Hitung ulang summary (subtotal dan PPN) dari awal
      // Note: This logic assumes we sum up ALL costs across all properties for the service subtotal
      // You might need to adjust this depending on whether 'summary' is per service or global
      const subtotal = service.details.properties.reduce((propSum, prop) => {
        const propCostSum =
          prop.costs?.reduce((sum, item) => {
            return sum + (Number(item.total_price) || 0);
          }, 0) || 0;
        return propSum + propCostSum;
      }, 0);

      const ppnAmount = subtotal * 0.11;

      const summary = state.createPKSInputBody.summary;

      summary.subtotal = subtotal;
      summary.taxes = [
        {
          tax_name: "PPN",
          rate_percent: 11,
          amount: ppnAmount,
        },
      ];
      summary.grand_total = subtotal + ppnAmount;
    },

    // INI BAGIAN PENTINGNYA: Action untuk mengubah tipe item
    updateCostType: (state, action) => {
      // Payload harus berisi index item dan tipe barunya
      const { serviceIndex, propIndex, itemIndex, newType } = action.payload;

      const service = state.createPKSInputBody.services[serviceIndex];

      // Ambil item yang akan diubah
      const itemToUpdate =
        service?.details?.properties?.[propIndex]?.costs?.[itemIndex];

      if (itemToUpdate) {
        // Update item_type
        itemToUpdate.cost_type = newType;
        // Ganti keseluruhan objek 'details' dengan struktur baru
        itemToUpdate.details = getDetailsCost(newType);

        if (newType === "Service Charge") {
          // Optimasi: Gunakan for loop untuk performa lebih baik
          const properties = [];
          let totalAmount = 0;

          // Filter services yang relevan
          const relevantServices = state.createPKSInputBody.services.filter(
            (item) =>
              item.service_type === "Retail" ||
              item.service_type === "Retail Station" ||
              item.service_type === "Retail Blok-M Hub",
          );

          // Proses setiap service dengan batch processing untuk performa
          const BATCH_SIZE = 50; // Proses maksimal 50 service per batch
          const servicesToProcess = relevantServices.slice(0, BATCH_SIZE);

          for (let i = 0; i < servicesToProcess.length; i++) {
            const serve = servicesToProcess[i];
            if (!serve.details || !serve.details.properties) continue;

            // Proses setiap property dengan limit juga
            const propertiesToProcess = serve.details.properties.slice(0, 100); // Maksimal 100 property

            for (let j = 0; j < propertiesToProcess.length; j++) {
              const prop = propertiesToProcess[j];
              if (!prop || !prop.unit) continue;

              // Hitung durasi sekali saja per property
              const startDate = dayjs(prop.start_date);
              const endDate = dayjs(prop.end_property_date);
              const durationMonths = endDate.diff(startDate, "month") + 1;

              // Prepare unit_price array
              const unitPrices = [];

              // Proses setiap unit
              for (let k = 0; k < prop.unit.length; k++) {
                const uni = prop.unit[k];
                if (!uni.price_subtotals || !Array.isArray(uni.price_subtotals))
                  continue;

                // Filter price_subtotals untuk service_charge
                const serviceChargeSubtotals = uni.price_subtotals.filter(
                  (price) => price.key === "service_charge",
                );

                if (serviceChargeSubtotals.length > 0) {
                  // Hitung total untuk unit ini
                  for (let l = 0; l < serviceChargeSubtotals.length; l++) {
                    const price = serviceChargeSubtotals[l];
                    const subtotal = Number(price.subtotal) || 0;

                    if (price.frequency === "Monthly") {
                      totalAmount += subtotal * durationMonths;
                    } else if (price.frequency === "Yearly") {
                      totalAmount += (subtotal / 12) * durationMonths;
                    }
                  }

                  unitPrices.push({
                    ...uni,
                    price_subtotals: serviceChargeSubtotals,
                  });
                }
              }

              if (unitPrices.length > 0) {
                properties.push({
                  unit_price: unitPrices,
                  start_date: prop.start_date,
                  end_property_date: prop.end_property_date,
                });
              }
            }
          }

          // Update details dengan hasil yang sudah dioptimasi - Proxy Safe
          try {
            if (itemToUpdate && itemToUpdate.details) {
              // Buat object baru untuk menghindari issue dengan proxy
              const safeProperties = JSON.parse(
                JSON.stringify(properties || []),
              );
              const safeTotal = Number(totalAmount) || 0;

              // Update dengan Object.assign untuk proxy compatibility
              Object.assign(itemToUpdate.details, {
                properties: safeProperties,
                total: safeTotal,
              });
            } else {
              console.error("❌ itemToUpdate atau details tidak valid");
            }
          } catch (error) {
            console.error("❌ Error updating details:", error.message);
          }

          // Update summary dengan PPN 11%
          const summary = state.createPKSInputBody.summary;
          const ppnAmount = totalAmount * 0.11;

          summary.subtotal = totalAmount;
          summary.total = totalAmount;
          summary.taxes = [
            {
              tax_name: "PPN",
              rate_percent: 11,
              amount: ppnAmount,
            },
          ];
          summary.grand_total = totalAmount + ppnAmount;
        }

        if (newType === "Rental Fee") {
          // Optimasi: Gunakan for loop untuk performa lebih baik
          const properties = [];
          let totalAmount = 0;

          // Filter services yang relevan
          const relevantServices = state.createPKSInputBody.services.filter(
            (item) =>
              item.service_type === "Retail" ||
              item.service_type === "Retail Station" ||
              item.service_type === "Retail Blok-M Hub",
          );

          // Proses setiap service dengan batch processing untuk performa
          const BATCH_SIZE = 50; // Proses maksimal 50 service per batch
          const servicesToProcess = relevantServices.slice(0, BATCH_SIZE);

          for (let i = 0; i < servicesToProcess.length; i++) {
            const serve = servicesToProcess[i];
            if (!serve.details || !serve.details.properties) continue;

            // Proses setiap property dengan limit juga
            const propertiesToProcess = serve.details.properties.slice(0, 100); // Maksimal 100 property

            for (let j = 0; j < propertiesToProcess.length; j++) {
              const prop = propertiesToProcess[j];
              if (!prop || !prop.unit) continue;

              // Hitung durasi sekali saja per property
              const startDate = dayjs(prop.start_date);
              const endDate = dayjs(prop.end_property_date);
              const durationMonths = endDate.diff(startDate, "month") + 1;

              // Prepare unit_price array
              const unitPrices = [];

              // Proses setiap unit
              for (let k = 0; k < prop.unit.length; k++) {
                const uni = prop.unit[k];
                if (!uni.price_subtotals || !Array.isArray(uni.price_subtotals))
                  continue;

                // Filter price_subtotals untuk service_charge
                const serviceChargeSubtotals = uni.price_subtotals.filter(
                  (price) => price.key === "rental_fee",
                );

                if (serviceChargeSubtotals.length > 0) {
                  // Hitung total untuk unit ini
                  for (let l = 0; l < serviceChargeSubtotals.length; l++) {
                    const price = serviceChargeSubtotals[l];
                    const subtotal = Number(price.subtotal) || 0;

                    if (price.frequency === "Monthly") {
                      totalAmount += subtotal * durationMonths;
                    } else if (price.frequency === "Yearly") {
                      totalAmount += (subtotal / 12) * durationMonths;
                    }
                  }

                  unitPrices.push({
                    ...uni,
                    price_subtotals: serviceChargeSubtotals,
                  });
                }
              }

              if (unitPrices.length > 0) {
                properties.push({
                  unit_price: unitPrices,
                  start_date: prop.start_date,
                  end_property_date: prop.end_property_date,
                });
              }
            }
          }

          // [SAFE UPDATE] Mencegah freeze dengan menangani proxy state object secara benar.
          try {
            if (itemToUpdate && itemToUpdate.details) {
              // Buat deep clone dari 'properties' untuk menghapus proxy
              // dan mencegah masalah mutasi.
              const safeProperties = JSON.parse(
                JSON.stringify(properties || []),
              );
              const safeTotal = Number(totalAmount) || 0;

              // Gunakan Object.assign untuk mengupdate proxy object 'details' dengan aman.
              // Ini akan memicu setter dari state management library dengan benar.
              Object.assign(itemToUpdate.details, {
                properties: safeProperties,
                total: safeTotal,
              });
            }
          } catch (error) {
            console.error(
              "❌ [PKS Reducer] Terjadi error saat mengupdate details:",
              error,
            );
          }

          // // Update summary dengan PPN 11%
          // const summary = state.createPKSInputBody.summary;
          // const ppnAmount = totalAmount * 0.11;

          // summary.subtotal = totalAmount;
          // summary.total = totalAmount;
          // summary.taxes = [
          //   {
          //     tax_name: "PPN",
          //     rate_percent: 11,
          //     amount: ppnAmount,
          //   },
          // ];
          // summary.grand_total = totalAmount + ppnAmount;
        }

        if (newType === "Utility Electricity Charge") {
          const cur = service?.details?.properties?.[propIndex]?.unit || [];

          const mapUnitForCost = (u) => ({
            unit_id: u._id || "",
            unit_number: u.unit_number || "",
            area_sqm: u.area_sqm || u.size_sqm || "",
            rate_periods: [
              { label: "", start_period: "", end_period: "", rate_per_sqm: "" },
            ],
          });

          itemToUpdate.details.units = cur.map(mapUnitForCost);
        }

        if (newType === "Utility Water Charge") {
          const cur = service?.details?.properties?.[propIndex]?.unit || [];

          const mapUnitForCost = (u) => ({
            unit_id: u._id || "",
            unit_number: u.unit_number || "",
            area_sqm: u.area_sqm || u.size_sqm || "",
            rate_periods: [
              { label: "", start_period: "", end_period: "", rate_per_sqm: "" },
            ],
          });

          itemToUpdate.details.units = cur.map(mapUnitForCost);
        }

        const subtotal = service.details.properties.reduce((propSum, prop) => {
          const propCostSum =
            prop.costs?.reduce((sum, item) => {
              return sum + (item.total || 0);
            }, 0) || 0;
          return propSum + propCostSum;
        }, 0);

        const ppnAmount = subtotal * 0.11; // 11% PPN

        // Update summary untuk Rental Fee
        const summary = state.createPKSInputBody.summary;
        summary.subtotal = subtotal;
        summary.taxes = [
          {
            tax_name: "PPN",
            rate_percent: 11,
            amount: ppnAmount,
          },
        ];
        summary.grand_total = subtotal + ppnAmount;
      }
    },

    updateObligation: (state, action) => {
      const { obligationIndex, value, field } = action.payload;
      state.createPKSInputBody[field].obligations[obligationIndex] = value;
    },

    // Reducer untuk MENAMBAH obligasi baru
    addObligation: (state, action) => {
      const { field } = action.payload;
      state.createPKSInputBody[field].obligations.push(""); // Tambah string kosong untuk input baru
    },

    // Reducer untuk MENGHAPUS obligasi
    removeObligation: (state, action) => {
      const { obligationIndex, field } = action.payload;
      // Hapus 1 elemen pada obligationIndex
      state.createPKSInputBody[field].obligations.splice(obligationIndex, 1);
    },

    updateRights: (state, action) => {
      const { obligationIndex, value, field } = action.payload;
      state.createPKSInputBody[field].rights[obligationIndex] = value;
    },

    // Reducer untuk MENAMBAH obligasi baru
    addRights: (state, action) => {
      const { field } = action.payload;

      state.createPKSInputBody[field].rights.push(""); // Tambah string kosong untuk input baru
    },

    // Reducer untuk MENGHAPUS obligasi
    removeRights: (state, action) => {
      const { obligationIndex, field } = action.payload;
      // Hapus 1 elemen pada obligationIndex
      state.createPKSInputBody[field].rights.splice(obligationIndex, 1);
    },

    setPartyEmails: (state, action) => {
      const { newEmails } = action.payload;
      state.createPKSInputBody.provider.emails = newEmails;
    },

    setPartyRepresentedBy: (state, action) => {
      const { representedBy } = action.payload;
      state.createPKSInputBody.provider.represented_by = representedBy;
    },

    // ── EXPIRY NOTIFICATION MANAGEMENT ────────────────────────────────────

    /**
     * Toggle enable/disable expiry notification
     */
    setExpiryNotificationEnabled: (state, action) => {
      state.createPKSInputBody.expiry_notification.is_enabled = action.payload;
    },

    /**
     * Set threshold days array
     * Payload: array of numbers, e.g., [90, 60, 30, 14, 7]
     */
    setExpiryNotificationThresholds: (state, action) => {
      state.createPKSInputBody.expiry_notification.threshold_days =
        action.payload;
    },

    /**
     * Add a single threshold day
     * Payload: number (days)
     */
    addExpiryNotificationThreshold: (state, action) => {
      const days = action.payload;
      const currentThresholds =
        state.createPKSInputBody.expiry_notification.threshold_days;
      if (!currentThresholds.includes(days)) {
        // Create new array, add item, and sort
        const newThresholds = [...currentThresholds, days].sort(
          (a, b) => b - a,
        );
        state.createPKSInputBody.expiry_notification.threshold_days =
          newThresholds;
      }
    },

    /**
     * Remove a threshold day
     * Payload: number (days)
     */
    removeExpiryNotificationThreshold: (state, action) => {
      const days = action.payload;
      state.createPKSInputBody.expiry_notification.threshold_days =
        state.createPKSInputBody.expiry_notification.threshold_days.filter(
          (d) => d !== days,
        );
    },

    /**
     * Set notification recipients
     * Payload: array of email strings
     */
    setExpiryNotificationRecipients: (state, action) => {
      state.createPKSInputBody.expiry_notification.recipients = action.payload;
    },

    /**
     * Add a recipient email
     * Payload: string (email)
     */
    addExpiryNotificationRecipient: (state, action) => {
      const email = action.payload;
      const currentRecipients =
        state.createPKSInputBody.expiry_notification.recipients;
      if (!currentRecipients.includes(email)) {
        state.createPKSInputBody.expiry_notification.recipients = [
          ...currentRecipients,
          email,
        ];
      }
    },

    /**
     * Remove a recipient email
     * Payload: string (email)
     */
    removeExpiryNotificationRecipient: (state, action) => {
      const email = action.payload;
      state.createPKSInputBody.expiry_notification.recipients =
        state.createPKSInputBody.expiry_notification.recipients.filter(
          (e) => e !== email,
        );
    },

    /**
     * Set notification channels
     * Payload: array of strings, e.g., ["email", "system", "whatsapp"]
     */
    setExpiryNotificationChannels: (state, action) => {
      state.createPKSInputBody.expiry_notification.notification_channels =
        action.payload;
    },

    /**
     * Toggle a notification channel
     * Payload: string (channel name)
     */
    toggleExpiryNotificationChannel: (state, action) => {
      const channel = action.payload;
      const currentChannels =
        state.createPKSInputBody.expiry_notification.notification_channels;
      const index = currentChannels.indexOf(channel);
      if (index > -1) {
        // Remove channel - create new array without it
        state.createPKSInputBody.expiry_notification.notification_channels =
          currentChannels.filter((c) => c !== channel);
      } else {
        // Add channel - create new array with it
        state.createPKSInputBody.expiry_notification.notification_channels = [
          ...currentChannels,
          channel,
        ];
      }
    },

    /**
     * Update expiry notification field
     * Payload: { field: string, value: any }
     */
    setExpiryNotificationField: (state, action) => {
      const { field, value } = action.payload;
      state.createPKSInputBody.expiry_notification[field] = value;
    },

    /**
     * Reset expiry notification to default
     */
    resetExpiryNotification: (state) => {
      state.createPKSInputBody.expiry_notification = {
        is_enabled: true,
        threshold_days: [90, 60, 30, 14, 7],
        recipients: [],
        notification_channels: ["email", "system"],
        last_notification_sent: null,
        notification_history: [],
      };
    },

    setClearCreatePKSInputBody: (state, _) => {
      return {
        ...state,
        createPKSInputBody: {
          pks_number: "",
          title: "",
          status: "Draft",
          contract_value: "",
          client: {
            signing_official: {
              name: "",
              title: "",
            },
            mitra: "",
            represented_by: [],
            obligations: [""],
            rights: [""],
            emails: [""],
          },
          provider: {
            // legal_name: "PT. MRT Jakarta",
            mitra: "",
            represented_by: [],
            obligations: [""],
            rights: [""],
            emails: [""],
            signing_official: {
              name: "",
              title: "",
            },
          },
          agreement_object: {
            field: "",
            description: "",
          },
          services: [
            {
              service: "",
              details: "",
            },
          ],
          summary: {
            // isPPNChage: false,
            subtotal: 0,
            discounts: [],
            taxes: [
              {
                tax_name: "PPN",
                rate_percent: 11,
                amount: 0,
              },
            ],
            grand_total: 0,
            currency: "IDR",
          },
          term: {
            option: "",
            period: "",
            duration: "",
            sign_document_date: "",
            payment_terms: "",
            effective_date: null,
            start_date: "",
            isSameEffectiveDate: false,
            end_date: null,
            total_period: "",
            efective_terms: [
              {
                term: "",
                efiden: {
                  file: "",
                  url: "",
                },
                due_date: "",
                status: "",
                isFinish: false,
              },
            ],
          },

          // Pengaturan notifikasi kontrak/PKS akan berakhir
          expiry_notification: {
            is_enabled: true,
            threshold_days: [90, 60, 30, 14, 7],
            recipients: [],
            notification_channels: ["email", "system"],
            last_notification_sent: null,
            notification_history: [],
          },

          termination_clause: {
            is_terminable: true,
            notice_period_days: 30,
          },
          legal_details: {
            addendum_clause:
              "Hal-hal yang belum diatur dalam surat perjanjian ini akan diatur kemudian oleh para pihak dalam bentuk addendum yang merupakan bagian yang tidak terpisahkan dari surat perjanjian ini.",
            document_copies: 2,
            is_stamped: true,
          },
          sanctionRules: {
            // Denda jika MRT terlambat/gagal menyediakan layanan infrastruktur
            late_performance: {
              payment_hold: {
                is_active: false,
                description: "",
              },
              penalty: {
                daily_rate_permil: 0, // per mil (‰) per hari
                max_penalty_percentage: 0,
              },
            },
            // Denda jika Bank DKI terlambat melakukan settlement/pembayaran
            late_payment: {
              daily_rate_percentage: 0,
              max_penalty_percentage: 0,
            },
            // Denda khusus downtime sistem Bank DKI melebihi SLA
            sla_breach: {
              is_active: false,
              threshold_hours: 4, // downtime melebihi X jam = denda
              penalty_per_incident: 0,
              max_incidents_per_month: 3,
            },
            // Proses teguran bertahap
            warning_process: [
              {
                step: 1,
                days_to_resolve: 7,
                description: "Surat Peringatan I",
              },
              {
                step: 2,
                days_to_resolve: 14,
                description: "Surat Peringatan II",
              },
              {
                step: 3,
                days_to_resolve: 30,
                description: "Surat Peringatan III / Terminasi",
              },
            ],
          },
          legal_document: [],
          pks_document_url: null,
          quotation_origin_id: null,
          isVerifyByFinance: null,
          created_by: null,
        },
      };
    },
    setSelectedPKS: (state, action) => {
      return {
        ...state,
        selectedPKS: action.payload,
      };
    },
    setCreatePKSInputBody: (state, action) => {
      return {
        ...state,
        createPKSInputBody: action.payload,
      };
    },
    setVerificationByHeadInputBody: (state, action) => {
      return {
        ...state,
        verificationByHeadInputBody: action.payload,
      };
    },

    setCurrentStep: (state, action) => {
      return {
        ...state,
        current_step: action.payload,
      };
    },
    setActiveStep: (state, action) => {
      return {
        ...state,
        active_step: action.payload,
      };
    },
    setTabValue: (state, action) => {
      return {
        ...state,
        tab_value: action.payload,
      };
    },

    // note: clear state
    setClearGetPKSObjectValue: (state, _) => {
      return {
        ...state,
        pks_object_value: "",
      };
    },
    setClearGetPKS: (state, _) => {
      return {
        ...state,
        getPKS: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
      };
    },
    setClearCreatePKS: (state, _) => {
      return {
        ...state,
        createPKS: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
      };
    },
    setClearUpdatePKS: (state, _) => {
      return {
        ...state,
        updatePKS: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
      };
    },
    setClearDeletePKS: (state, _) => {
      return {
        ...state,
        deletePKS: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
      };
    },
    setClearAllPKSState: (state, _) => {
      return {
        ...state,
        // Get PKS inisial state
        getPKS: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
        // Create PKS inisial state
        createPKS: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
        // Update PKS inisial state
        updatePKS: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
        // Delete PKS inisial state
        deletePKS: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
        // PKS object value
        pks_object_value: "",
      };
    },

    // ── UNIT PRICE LIST ───────────────────────────────────────────────────────
    // Tambah entry baru ke unitPriceList unit tertentu di dalam property
    addUnitPriceItem: (state, action) => {
      const { serviceIndex, propIndex, unitIndex } = action.payload;
      const unit =
        state.createPKSInputBody.services[serviceIndex]?.details?.properties?.[
          propIndex
        ]?.unit?.[unitIndex];

      if (unit) {
        if (!Array.isArray(unit.price_subtotals)) {
          unit.price_subtotals = [];
        }
        unit.price_subtotals.push({
          key: "",
          label: "",
          price: 0,
          qty: 0,
          unit: "",
          subtotal: 0,
          currency: "IDR",
          frequency: "Monthly",
        });
      }
    },

    // Hapus entry dari price_subtotals
    removeUnitPriceItem: (state, action) => {
      const { serviceIndex, propIndex, unitIndex, priceIndex } = action.payload;
      const unit =
        state.createPKSInputBody.services[serviceIndex]?.details?.properties?.[
          propIndex
        ]?.unit?.[unitIndex];

      if (unit && Array.isArray(unit.price_subtotals)) {
        unit.price_subtotals.splice(priceIndex, 1);
      }
    },

    // Update field tertentu pada entry price_subtotals
    updateUnitPriceItem: (state, action) => {
      const { serviceIndex, propIndex, unitIndex, priceIndex, field, value } =
        action.payload;
      const unit =
        state.createPKSInputBody.services[serviceIndex]?.details?.properties?.[
          propIndex
        ]?.unit?.[unitIndex];

      if (
        unit &&
        Array.isArray(unit.price_subtotals) &&
        unit.price_subtotals[priceIndex]
      ) {
        if (field === "charge" || field === "label") {
          const KEY_MAP = {
            "Rental Fee": "rental_fee",
            "Service Charge": "service_charge",
            Electricity: "electricity",
            Water: "water",
          };
          unit.price_subtotals[priceIndex].label = value;
          unit.price_subtotals[priceIndex].key =
            KEY_MAP[value] || value.toLowerCase().replace(/\s+/g, "_");

          // Setelan default quantity berdasar charge
          if (value === "Rental Fee" || value === "Service Charge") {
            const qty = Number(unit.area_sqm || unit.size_sqm || 0);
            unit.price_subtotals[priceIndex].qty = qty;
            unit.price_subtotals[priceIndex].unit = "sqm";
            unit.price_subtotals[priceIndex].subtotal =
              (unit.price_subtotals[priceIndex].price || 0) * qty;
          } else {
            unit.price_subtotals[priceIndex].qty = 0;
            unit.price_subtotals[priceIndex].unit = "";
            unit.price_subtotals[priceIndex].subtotal = 0;
          }
        } else if (field === "price") {
          unit.price_subtotals[priceIndex].price = value;
          unit.price_subtotals[priceIndex].subtotal =
            value * (unit.price_subtotals[priceIndex].qty || 0);
        } else if (field === "qty") {
          unit.price_subtotals[priceIndex].qty = value;
          unit.price_subtotals[priceIndex].subtotal =
            (unit.price_subtotals[priceIndex].price || 0) * value;
        } else {
          unit.price_subtotals[priceIndex][field] = value;
        }
      }
    },
  },
  extraReducers(builder) {
    builder
      // Get PKS reducer
      .addCase(getPKSThunk.pending, (state, _) => {
        return {
          ...state,
          getPKS: {
            data: null,
            isPending: true,
            isFulfilled: false,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(getPKSThunk.fulfilled, (state, action) => {
        return {
          ...state,
          getPKS: {
            data: action.payload,
            isPending: false,
            isFulfilled: true,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(getPKSThunk.rejected, (state, action) => {
        return {
          ...state,
          getPKS: {
            data: null,
            isPending: false,
            isFulfilled: false,
            isRejected: true,
            err: action.error.message,
          },
        };
      })
      .addCase(getPKSByIdThunk.pending, (state, _) => {
        return {
          ...state,
          getPKSById: {
            data: null,
            isPending: true,
            isFulfilled: false,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(getPKSByIdThunk.fulfilled, (state, action) => {
        return {
          ...state,
          getPKSById: {
            data: action.payload,
            isPending: false,
            isFulfilled: true,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(getPKSByIdThunk.rejected, (state, action) => {
        return {
          ...state,
          getPKSById: {
            data: null,
            isPending: false,
            isFulfilled: false,
            isRejected: true,
            err: action.error.message,
          },
        };
      })
      // Create PKS reducer
      .addCase(createPKSThunk.pending, (state, _) => {
        return {
          ...state,
          createPKS: {
            data: null,
            isPending: true,
            isFulfilled: false,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(createPKSThunk.fulfilled, (state, action) => {
        return {
          ...state,
          createPKS: {
            data: action.payload,
            isPending: false,
            isFulfilled: true,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(createPKSThunk.rejected, (state, action) => {
        return {
          ...state,
          createPKS: {
            data: null,
            isPending: false,
            isFulfilled: false,
            isRejected: true,
            err: action.error.message,
          },
        };
      })
      // update PKS reducer
      .addCase(updatePKSThunk.pending, (state, _) => {
        return {
          ...state,
          updatePKS: {
            data: null,
            isPending: true,
            isFulfilled: false,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(updatePKSThunk.fulfilled, (state, action) => {
        return {
          ...state,
          updatePKS: {
            data: action.payload,
            isPending: false,
            isFulfilled: true,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(updatePKSThunk.rejected, (state, action) => {
        return {
          ...state,
          updatePKS: {
            data: null,
            isPending: false,
            isFulfilled: false,
            isRejected: true,
            err: action.error.message,
          },
        };
      }) // update PKS reducer
      .addCase(verifyPKSByHeadThunk.pending, (state, _) => {
        return {
          ...state,
          verifyPKSByHead: {
            data: null,
            isPending: true,
            isFulfilled: false,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(verifyPKSByHeadThunk.fulfilled, (state, action) => {
        return {
          ...state,
          verifyPKSByHead: {
            data: action.payload,
            isPending: false,
            isFulfilled: true,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(verifyPKSByHeadThunk.rejected, (state, action) => {
        return {
          ...state,
          verifyPKSByHead: {
            data: null,
            isPending: false,
            isFulfilled: false,
            isRejected: true,
            err: action.error.message,
          },
        };
      });
  },
});

export const pksAction = {
  ...pksSlice.actions,
  getPKSThunk,
  createPKSThunk,
  updatePKSThunk,
  deletePKSThunk,
  verifyPKSByHeadThunk,
  getPKSByIdThunk,
  bulkCreatePKSThunk,
};

export function selectPKSObjectValue(state) {
  return state?.pks?.pks_object_value;
}

export function selectPKS(state) {
  return state?.pks;
}

// Asumsi state Anda adalah state.pks.pksInputBody
const selectPksTerm = (state) => state.pks.createPKSInputBody.term;

// Selector untuk menghitung periode grant fee secara otomatis
export const selectGrantFeePeriods = createSelector(
  [selectPksTerm], // Input selector
  (term) => {
    // Fungsi ini hanya akan berjalan jika state 'term' berubah
    // dan akan otomatis menghitung ulang periode
    return getGrantFeePeriodsDayjs(term.start_date, term.end_date);
  },
);

export default pksSlice.reducer;
